import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import api from "./api/axios";
import { getMmlSheet } from "./utils/mmlSheetFetch";
import { helperLastRequiredDay, isHelperLogComplete } from "./utils/formCompletion";
import { toDateOnlyValue, formatIsoDateMedium, formatStampShort, NICU_DAY_GRACE_HOUR, nicuDayNumberFromDay1, nicuDayForCalendarYmd, helperDayStripLength } from "./utils/datetime";
import DayOfLifeNote from "./components/helper/DayOfLifeNote";
import "./styles/RespCVNeuro.css";
import "./styles/MinimalMonitoring.css";
import { usePatient } from "./context/PatientContext";
import { useFormProgress } from "./context/FormProgressContext";
import { useAuth } from "./context/AuthContext";
import SaveSuccessModal from "./components/SaveSuccessModal";
import AuditChangeList from "./components/AuditChangeList";
import { auditActionLabel } from "./utils/auditDiff";
import { useRegisterActiveFormSession } from "./context/ActiveFormSessionContext";
import { normalizeHelperDob } from "./hooks/useHelperDobSyncDay1";
import { mmlSyncGlucoseFieldFromMml } from "./utils/mmlHelperSync";
import { rememberActiveDay, HELPER_SESSION_KEY_METAB_RENAL_VASC_EYE } from "./utils/helperSession";
import { apiDetailMessage, expectedUpdatedAtConfig, isStaleWrite, STALE_WRITE_MESSAGE } from "./utils/staleWrite";
import { BLANK_HELPER_DAY_CLIENT_MESSAGE, isBlankHelperDay, isBlankHelperDayRefusal } from "./utils/blankHelperDay";
import { helperLocationOptions, locationLabel, normalizeHelperLocations } from "./utils/helperLocation";
import { helperDayDisplayStatus, helperDaySaveStatus } from "./utils/helperDayStatus";
import { useDefaultToWorkingNicuDay, useNicuWorkingDay } from "./hooks/useNicuWorkingDay";
import {
  ArrowLeft, ArrowRight, Save, ChevronDown,
  CheckCircle, AlertTriangle, X, Clock, Check,
  Lock, Edit,
  AlertOctagon, Unlock, History, RefreshCw, Plus, Trash2, ListChecks, Calendar,
} from "lucide-react";

/* ══════════════════════════════════════════════════════
   STATUS CONSTANTS — identical to Helper Forms 1 & 3
══════════════════════════════════════════════════════ */
const STATUS = {
  EMPTY:"empty", DRAFT:"draft", PARTIAL:"partial",
  COMPLETE:"complete", SUBMITTED:"submitted", LATE:"late",
};
const DAY_STATUS_CONFIG = {
  [STATUS.EMPTY]:    { label:"Not started", color:"#CBD5E1", dot:"#CBD5E1" },
  [STATUS.DRAFT]:    { label:"Partial",     color:"#F59E0B", dot:"#F59E0B" },
  [STATUS.PARTIAL]:  { label:"Partial",     color:"#F59E0B", dot:"#F59E0B" },
  [STATUS.COMPLETE]: { label:"Complete",    color:"#10B981", dot:"#10B981" },
  [STATUS.SUBMITTED]:{ label:"Submitted",   color:"#0F4C81", dot:"#0F4C81" },
  [STATUS.LATE]:     { label:"Late",        color:"#EF4444", dot:"#EF4444" },
};
const LEGEND_ITEMS = [
  { label:"Not started", dot:"#CBD5E1" },
  { label:"Partial",     dot:"#F59E0B" },
  { label:"Complete",    dot:"#10B981" },
  { label:"Submitted",   dot:"#0F4C81" },
  { label:"Late",        dot:"#EF4444" },
  { label:"Locked",      dot:"#94A3B8", lock: true },
];

/* Every field captured for a day, grouped by section, for the
   "All Days — Table View" modal (fields run down the rows, days
   run across the columns). Same pattern as Helper Forms 1 & 3. */
const TABLE_VIEW_FIELD_GROUPS = [
  {
    section: "Metabolic",
    rows: [
      { key: "lowest_glucose",        label: "Lowest Glucose", suffix: "mg/dL" },
      { key: "hypoglycemia_episodes", label: "Hypoglycemia Episodes" },
      { key: "hypoglycemia_rx",       label: "Hypoglycemia Rx", bool: true },
      { key: "highest_glucose",       label: "Highest Glucose", suffix: "mg/dL" },
      { key: "insulin",               label: "Hyperglycemia Rx (Insulin)", bool: true },
      { key: "metabolic_acidosis",    label: "Metabolic Acidosis", bool: true, statusKey: "metabolic_acidosis_status" },
      { key: "sodium_value",          label: "Sodium Value", suffix: "mmol/L" },
      { key: "potassium_value",       label: "Potassium Value", suffix: "mmol/L" },
      { key: "ionized_calcium_value", label: "Ionized Calcium Value", suffix: "mmol/L" },
      { key: "osteopenia_suspected",  label: "Osteopenia Suspected", bool: true },
    ],
  },
  {
    section: "Renal",
    rows: [
      { key: "aki_suspected",         label: "AKI Suspected", bool: true },
      { key: "creatinine_value",      label: "Serum Creatinine" },
      { key: "urine_output_8am_2pm",  label: "UO 8am–2pm", suffix: " ml/kg/hr", statusKey: "urine_output_8am_2pm_status" },
      { key: "urine_output_2pm_8pm",  label: "UO 2pm–8pm", suffix: " ml/kg/hr", statusKey: "urine_output_2pm_8pm_status" },
      { key: "urine_output_8pm_8am",  label: "UO 8pm–8am", suffix: " ml/kg/hr", statusKey: "urine_output_8pm_8am_status" },
      { key: "urine_output_total",    label: "Urine Output Total", suffix: " ml/kg/hr" },
      { key: "dialysis_crrt",         label: "Dialysis/CRRT", bool: true },
    ],
  },
  {
    section: "Thermoregulation",
    rows: [
      { key: "axillary_temperature",  label: "Axillary Temperature", suffix: "°C" },
    ],
  },
  {
    section: "Vascular Access",
    rows: [
      { key: "picc_in_situ",          label: "PICC In Situ", bool: true },
      { key: "uvc_in_situ",           label: "UVC In Situ", bool: true },
      { key: "uac_in_situ",           label: "UAC In Situ", bool: true },
      { key: "peripheral_iv",         label: "Peripheral IV", bool: true },
      { key: "peripheral_arterial",   label: "Peripheral Arterial", bool: true },
      { key: "extravasation_injury",  label: "Extravasation Injury", bool: true },
      { key: "line_complication",     label: "Line Complication", bool: true },
    ],
  },
  {
    section: "Ophthalmology (ROP)",
    rows: [
      { key: "rop_screening_due",     label: "ROP Screening Due", bool: true },
      { key: "rop_screened",          label: "ROP Screened", bool: true },
      { key: "rop_detected",          label: "ROP Detected", bool: true },
      { key: "plus_disease",          label: "Plus Disease", bool: true },
      { key: "rop_treatment",         label: "ROP Treatment", bool: true },
    ],
  },
  {
    section: "Location & Outcome",
    rows: [
      { key: "location",              label: "Location" },
      { key: "survived_the_day",      label: "Survived the Day", bool: true },
    ],
  },
  {
    section: "Record",
    rows: [
      { key: "saved_by", label: "Saved By" },
    ],
  },
];

/* Formats a single field's value for one day's data object `d`. */
function formatTableViewValue(d, row) {
  // "Not Recorded / Not Done" (etc.) lives in a sibling *_status column,
  // not in the field itself — check that first, since the underlying
  // value is null whenever a status is set.
  if (row.statusKey && d[row.statusKey]) return d[row.statusKey];
  const v = d[row.key];
  if (row.bool) return v === true ? "Yes" : v === false ? "No" : "—";
  if (v === null || v === undefined || v === "") return "—";
  return row.suffix ? `${v}${row.suffix}` : String(v);
}

/* Given one day's saved record `d`, returns the fields that are still
   blank, grouped by section — reuses the same TABLE_VIEW_FIELD_GROUPS
   key/label map the "All Days" table view already relies on, so the two
   views can never disagree about what counts as "answered". The "Record"
   section is metadata (saved_by), not something a nurse fills in, so it's
   excluded from the missing-fields count. Same pattern as Helper Form 1. */
function computeMissingFields(d) {
  if (!d) return [];
  return TABLE_VIEW_FIELD_GROUPS
    .filter(group => group.section !== "Record")
    .map(group => ({
      section: group.section,
      labels: group.rows
        .filter(row => formatTableViewValue(d, row) === "—")
        .map(row => row.label),
    }))
    .filter(group => group.labels.length > 0);
}

/* ══════════════════════════════════════════════════════
   SHARED SUB-COMPONENTS — identical to Helper Forms 1 & 3
══════════════════════════════════════════════════════ */
function ProgressRing({ percent }) {
  const r = 24, circ = 2 * Math.PI * r;
  return (
    <div className="rcn-ring">
      <svg width="58" height="58" viewBox="0 0 58 58">
        <circle className="rcn-ring-bg" cx="29" cy="29" r={r} />
        <circle className="rcn-ring-fill" cx="29" cy="29" r={r}
          strokeDasharray={circ}
          strokeDashoffset={circ - (percent / 100) * circ}
          style={{ transform:"rotate(-90deg)", transformOrigin:"50% 50%" }}
        />
      </svg>
      <span className="rcn-ring-text">{percent}%</span>
    </div>
  );
}

function YNRow({ label, value, onChange, disabled }) {
  return (
    <div className="rcn-yn-row">
      <span className="rcn-yn-label">{label}</span>
      <div className="rcn-yn">
        <button type="button"
          className={`rcn-yn-btn rcn-yn-yes${value === true ? " rcn-yn-active-yes" : ""}`}
          onClick={() => !disabled && onChange(value === true ? null : true)}
          disabled={disabled}>Yes</button>
        <button type="button"
          className={`rcn-yn-btn rcn-yn-no${value === false ? " rcn-yn-active-no" : ""}`}
          onClick={() => !disabled && onChange(value === false ? null : false)}
          disabled={disabled}>No</button>
      </div>
    </div>
  );
}

const STATUS_AWAITED  = "Result Awaited";
const STATUS_NOT_DONE = "Not Recorded / Not Done";
const STATUS_NOT_TESTED = "Not Tested";
const isStatusSentinel = v => v === STATUS_AWAITED || v === STATUS_NOT_DONE;
const isGlucoseSentinel = v =>
  v === STATUS_NOT_TESTED || v === "Not Low" || v === "Not High";

/** Small toggle-button pair rendered next to a field label. */
function StatusToggleGroup({
  status, onChange, disabled, allowAwaited = false,
  notDoneLabel = STATUS_NOT_DONE, notDoneValue = STATUS_NOT_DONE,
}) {
  const isAwaited = status === STATUS_AWAITED;
  const isNotDone = status === notDoneValue;
  return (
    <div className="rcn-status-toggle-group">
      {allowAwaited && (
        <button type="button"
          className={`rcn-notdone-toggle rcn-awaited-toggle${isAwaited ? " rcn-awaited-toggle--on" : ""}`}
          onClick={() => !disabled && onChange(STATUS_AWAITED)}
          disabled={disabled}
        >{isAwaited ? "Undo" : "Result Awaited"}</button>
      )}
      <button type="button"
        className={`rcn-notdone-toggle${isNotDone ? " rcn-notdone-toggle--on" : ""}`}
        onClick={() => !disabled && onChange(notDoneValue)}
        disabled={disabled}
      >{isNotDone ? "Undo" : notDoneLabel}</button>
    </div>
  );
}

function NumRow({ label, value, onChange, disabled, unit, placeholder="0",
  status = null, onStatusChange = null, allowAwaited = false, autofilled = false }) {
  const isAwaited  = status === STATUS_AWAITED;
  const isNotDone  = status === STATUS_NOT_DONE;
  const isSentinel = isAwaited || isNotDone;
  const toggle = (s) => {
    if (disabled || !onStatusChange) return;
    onStatusChange(status === s ? null : s);
    if (status !== s && value !== null && value !== undefined && value !== "") onChange(null);
  };
  return (
    <div className={`rcn-yn-row${autofilled ? " rcn-autofilled-row" : ""}`}>
      <div className="rcn-field-label-row">
        <span className="rcn-yn-label">
          {label}
          {autofilled && <span className="rcn-autofill-tag">Auto-filled from Daily Monitoring Sheet</span>}
        </span>
        {onStatusChange && (
          <StatusToggleGroup status={status} onChange={toggle} disabled={disabled} allowAwaited={allowAwaited} />
        )}
      </div>
      <div className={`rcn-num-input${autofilled ? " rcn-num-input--autofill" : ""}${isSentinel ? ` rcn-num-input--sentinel ${isAwaited ? "rcn-num-input--awaited" : "rcn-num-input--notdone"}` : ""}`} style={{ width:160 }}>
        {isSentinel ? (
          <span className="rcn-num-sentinel-text">{status}</span>
        ) : (
          <>
            <input type="number" min="0" step="0.01"
              placeholder={placeholder}
              value={value ?? ""}
              onChange={e => !disabled && onChange(e.target.value === "" ? null : Number(e.target.value))}
              readOnly={disabled}
            />
            {unit && <span className="rcn-num-unit">{unit}</span>}
          </>
        )}
      </div>
    </div>
  );
}

/** Text/numeric row for creatinine / glucose — accepts a number, or a
 *  sentinel set via the toggle ("Result Awaited", "Not Recorded / Not
 *  Done", or glucose "Not Tested"). Sentinels never live in the input. */
function GlucoseTextRow({ label, value, onChange, disabled, unit, autofilled, placeholder = "—", allowAwaited = false, allowNotDone = false, allowNotTested = false }) {
  const isGlucoseStatus = allowNotTested && isGlucoseSentinel(value);
  const isSentinel = isStatusSentinel(value) || isGlucoseStatus;
  const isAwaited  = value === STATUS_AWAITED;
  const toggle = (s) => {
    if (disabled) return;
    if (allowNotTested && (s === STATUS_NOT_TESTED || isGlucoseSentinel(value))) {
      onChange(isGlucoseSentinel(value) ? null : STATUS_NOT_TESTED);
      return;
    }
    onChange(value === s ? null : s);
  };
  const toggleStatus = isGlucoseStatus
    ? STATUS_NOT_TESTED
    : (isStatusSentinel(value) ? value : null);
  return (
    <div className={`rcn-yn-row${autofilled ? " rcn-autofilled-row" : ""}`}>
      <div className="rcn-field-label-row">
        <span className="rcn-yn-label">
          {label}
          {autofilled && <span className="rcn-autofill-tag">Auto-filled from Daily Monitoring Sheet</span>}
        </span>
        {(allowAwaited || allowNotDone || allowNotTested) && (
          <StatusToggleGroup
            status={toggleStatus}
            onChange={toggle}
            disabled={disabled}
            allowAwaited={allowAwaited}
            notDoneLabel={allowNotTested ? "Not Tested" : STATUS_NOT_DONE}
            notDoneValue={allowNotTested ? STATUS_NOT_TESTED : STATUS_NOT_DONE}
          />
        )}
      </div>
      <div className={`rcn-num-input${autofilled ? " rcn-num-input--autofill" : ""}${isSentinel ? ` rcn-num-input--sentinel ${isAwaited ? "rcn-num-input--awaited" : "rcn-num-input--notdone"}` : ""}`} style={{ width: 180 }}>
        {isSentinel ? (
          <span className="rcn-num-sentinel-text">{value}</span>
        ) : (
          <>
            <input
              type="text"
              value={value ?? ""}
              onChange={e => !disabled && onChange(e.target.value === "" ? null : e.target.value)}
              readOnly={disabled}
              placeholder={placeholder}
            />
            {unit && <span className="rcn-num-unit">{unit}</span>}
          </>
        )}
      </div>
    </div>
  );
}


/** Display-only autofill field (#1/#2/#4) — never a free-text input. */
function ReadonlyAutoField({ label, value, unit, autofilled }) {
  const display = value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className={`rcn-yn-row${autofilled ? " rcn-autofilled-row" : ""}`}>
      <span className="rcn-yn-label">
        {label}
        {autofilled && <span className="rcn-autofill-tag">Auto-filled from Daily Monitoring Sheet</span>}
      </span>
      <div className={`rcn-readonly-value${autofilled ? " rcn-num-input--autofill" : ""}`}>
        {display}{unit && display !== "—" ? ` ${unit}` : ""}
      </div>
    </div>
  );
}

const pad2 = n => String(n).padStart(2, "0");
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const nowTime = (d = new Date()) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

function blankReading(extra = {}) {
  const d = new Date();
  return { id: uid(), date: toDateOnlyValue(d), time: nowTime(d), ...extra };
}

function parseJsonArray(raw) {
  if (!raw) return null;
  try {
    const p = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(p) && p.length ? p : null;
  } catch (_) {
    return null;
  }
}

/** "7:30", "7.30", "7:30 pm" -> "07:30" / "19:30". The tablet's reading time
 *  was free text until 2026-10-21 and <input type="time"> shows anything that
 *  is not exactly HH:MM as blank. Unparseable text is returned unchanged. */
function normalizeReadingTime(raw) {
  const t = String(raw ?? "").trim();
  const m = t.match(/^(\d{1,2})[:.](\d{2})\s*([ap])?\.?\s*m?\.?$/i);
  if (!m) return t;
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ap = (m[3] || "").toLowerCase();
  if (ap === "p" && h < 12) h += 12;
  if (ap === "a" && h === 12) h = 0;
  if (h > 23 || min > 59) return t;
  return `${pad2(h)}:${pad2(min)}`;
}

/** Readings JSON from the server, with times made displayable. */
function parseReadings(raw) {
  const list = parseJsonArray(raw);
  return list && list.map(r => (r && r.time ? { ...r, time: normalizeReadingTime(r.time) } : r));
}

/** Summary for electrolyte columns: most recent non-empty value. */
function latestReadingSummary(readings, valueKey = "value") {
  if (!Array.isArray(readings)) return null;
  for (let i = readings.length - 1; i >= 0; i--) {
    const v = readings[i]?.[valueKey];
    if (v !== null && v !== undefined && v !== "") return String(v);
  }
  return null;
}

function deriveMetabolicAcidosis(readings) {
  if (!Array.isArray(readings) || !readings.length) return null;
  let any = false;
  for (const r of readings) {
    if (r?.ph === "" || r?.ph == null) continue;
    const n = Number(r.ph);
    if (!Number.isFinite(n)) continue;
    any = true;
    if (n < 7.2) return true;
  }
  return any ? false : null;
}

function isNumericHighGlucose(v) {
  if (v == null || v === "" || v === "Not Tested" || v === "Not High" || v === "Not Low") return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 125;
}

function computeUrineTotal(a, b, c) {
  const nums = [a, b, c]
    .map(v => (v === null || v === undefined || v === "" ? null : Number(v)))
    .filter(n => Number.isFinite(n));
  if (!nums.length) return null;
  const sum = nums.reduce((s, n) => s + n, 0);
  return String(Math.round(sum * 1000) / 1000);
}

function migrateAkiFromLegacy(d) {
  // KDIGO stage is no longer captured on this form (removed) — this only
  // derives aki_suspected, including from older rows that pre-date the
  // aki_suspected/aki_stage split and only had a combined "N"/"Stage X" value.
  if (d.aki_suspected === true || d.aki_suspected === false) {
    return { aki_suspected: d.aki_suspected };
  }
  if (d.aki_stage === "N") return { aki_suspected: false };
  if (d.aki_stage && String(d.aki_stage).startsWith("Stage")) {
    return { aki_suspected: true };
  }
  return { aki_suspected: null };
}

/** Helper-5-style multi-entry block (date/time + value field(s)). */
function ReadingsBlock({
  code, entries, onChangeEntry, onAdd, onRemove, disabled, blankFactory, children, fixedDate,
}) {
  const list = entries?.length ? entries : [blankFactory ? blankFactory() : blankReading()];
  return (
    <div className="rcn-readings" aria-label={`${code} readings`}>
      {list.map((entry, idx) => (
        <div className="rcn-reading-row" key={entry.id || idx}>
          {list.length > 1 && <span className="rcn-reading-idx">#{idx + 1}</span>}
          <label className="mml-meta-field">
            <span>Date</span>
            <input
              type="date"
              className="rcn-text-input mml-date-input"
              value={fixedDate || entry.date || ""}
              readOnly={!!fixedDate}
              disabled={disabled || !!fixedDate}
              onChange={e => {
                if (fixedDate) return;
                onChangeEntry(idx, "date", e.target.value);
              }}
            />
          </label>
          <label className="mml-meta-field">
            <span>Time</span>
            <input type="time" className="rcn-text-input mml-time-input" value={entry.time || ""}
              disabled={disabled} onChange={e => onChangeEntry(idx, "time", e.target.value)} />
          </label>
          {children(entry, idx)}
          {list.length > 1 && !disabled && (
            <button type="button" className="mml-remove-btn" title="Remove this reading"
              onClick={() => onRemove(idx)}><Trash2 size={14} /></button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" className="mml-add-btn"
          onClick={() => onAdd(blankFactory ? blankFactory() : blankReading())}>
          <Plus size={14} /> Add values
        </button>
      )}
    </div>
  );
}

function SectionCard({ iconEmoji, title, answered, total, children, defaultOpen=true, headerAction=null }) {
  const [open, setOpen] = useState(defaultOpen);
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  return (
    <div className="rcn-card">
      <div className="rcn-card-header" onClick={() => setOpen(o => !o)}>
        <div className="rcn-card-header-left">
          <div className="rcn-card-icon-wrap">
            <span className="rcn-card-emoji">{iconEmoji}</span>
          </div>
          <h3 className="rcn-card-title">{title}</h3>
          {headerAction && (
            <div className="rcn-card-header-action" onClick={e => e.stopPropagation()}>
              {headerAction}
            </div>
          )}
        </div>
        <div className="rcn-card-header-right">
          <div className="rcn-card-prog-bar">
            <div className="rcn-card-prog-fill" style={{ width:`${pct}%` }} />
          </div>
          <span className="rcn-card-prog-text">{answered}/{total}</span>
          <div className={`rcn-chevron${open ? " rcn-chevron-open" : ""}`}>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>
      {open && (
        <>
          <div className="rcn-card-divider" />
          <div className="rcn-card-body">{children}</div>
        </>
      )}
    </div>
  );
}

/* ── DMS 5.3.A → Helper 5 glucose autofill ─────────────────────────
   DMS met_a[].glucose → Helper 5 fields #1, #2, #4.
   Boundaries: <45 low, 45–125 normal, >125 high (inclusive normal).
   >125 matches Form H / PI-confirmed cutoff (CRF text >180 was a documentation error). */
const GLUCOSE_LOW_MAX = 45;
const GLUCOSE_HIGH_MIN = 125;

function parseMetAGlucoseReadings(payload) {
  let entries = payload?.entries_json;
  if (typeof entries === "string") {
    try { entries = JSON.parse(entries); } catch (_) { entries = null; }
  }
  const list = entries?.met_a;
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const row of list) {
    const raw = row?.glucose;
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    out.push(n);
  }
  return out;
}

function computeGlucoseAutofill(readings) {
  if (!readings.length) {
    return {
      lowest_glucose: "Not Tested",
      hypoglycemia_episodes: 0,
      highest_glucose: "Not Tested",
    };
  }
  const lows = readings.filter(v => v < GLUCOSE_LOW_MAX);
  const highs = readings.filter(v => v > GLUCOSE_HIGH_MIN);
  return {
    lowest_glucose: lows.length
      ? String(Math.min(...lows))
      : "Not Low",
    hypoglycemia_episodes: lows.length,
    highest_glucose: highs.length
      ? String(Math.max(...highs))
      : "Not High",
  };
}

function glucoseFieldLooksMmlSourced(current, fieldKey, readings) {
  const computed = computeGlucoseAutofill(readings);
  return String(current ?? "").trim() === String(computed[fieldKey] ?? "").trim();
}

function isEmptyMetabField(v) {
  return v === null || v === undefined || v === "";
}

const listToString = v => Array.isArray(v) ? v.join(",") : (v || "");
const stringToList = v => Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean);

/* Multi-select pills (Dyselectrolytemia type) */
function PillMulti({ options, value=[], onChange, disabled, labelOf = (o) => o }) {
  const toggle = (opt) => {
    if (disabled) return;
    const next = value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt];
    onChange(next);
  };
  return (
    <div className="rcn-pills">
      {options.map(opt => (
        <button key={opt} type="button"
          className={`rcn-pill${value.includes(opt) ? " rcn-pill--on" : ""}`}
          onClick={() => toggle(opt)} disabled={disabled}>
          {labelOf(opt)}
        </button>
      ))}
    </div>
  );
}

/* Single-select pills (e.g. Location) */
function PillSingle({ options, value, onChange, disabled }) {
  return (
    <div className="rcn-pills">
      {options.map(opt => (
        <button key={opt} type="button"
          className={`rcn-pill${value === opt ? " rcn-pill--on" : ""}`}
          onClick={() => !disabled && onChange(value === opt ? null : opt)}
          disabled={disabled}>
          {opt}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   HELPER FUNCTIONS FOR GESTATION
══════════════════════════════════════════════════════ */
const totalGestationDays = (weeks, days) => {
  if (weeks === null || weeks === undefined || weeks === "") return null;
  if (days === null || days === undefined || days === "") return null;
  const w = Number(weeks);
  const d = Number(days);
  return Number.isNaN(w) || Number.isNaN(d) ? null : w * 7 + d;
};

const formatGestation = (weeks, days) =>
  weeks !== null && weeks !== undefined && weeks !== "" ? `${weeks}+${days ?? 0} wks` : "";

function SubmitModal({ day, completionPct, onConfirm, onCancel, submitting }) {
  return (
    <div className="rcn-modal-overlay">
      <div className="rcn-modal">
        <div className="rcn-modal-header">
          <div className="rcn-modal-icon rcn-modal-icon--lock"><Lock size={22}/></div>
          <div>
            <h3 className="rcn-modal-title">Lock Day {day} Data</h3>
            <p className="rcn-modal-subtitle">This record will become read-only</p>
          </div>
          <button className="rcn-modal-close" onClick={onCancel} type="button"><X size={18} /></button>
        </div>
        <div className="rcn-modal-body">
          <div className="rcn-modal-checklist">
            <div className={`rcn-modal-check ${completionPct===100 ? "rcn-modal-check--ok" : "rcn-modal-check--warn"}`}>
              {completionPct===100 ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>}
              <span>{completionPct===100 ? "All fields completed (100%)" : `${completionPct}% complete — some fields unanswered`}</span>
            </div>
            <div className="rcn-modal-check rcn-modal-check--ok">
              <CheckCircle size={15}/><span>Nurse data entry saved</span>
            </div>
          </div>
          <div className="rcn-modal-warning rcn-modal-warning--lock">
            <Lock size={14} />
            <span>
              After locking, you will not be able to edit this data.
              Are you sure you want to lock Day {day}?
            </span>
          </div>
          {completionPct < 100 && (
            <div className="rcn-modal-warning">
              <AlertTriangle size={14}/>
              <span>Locking with incomplete data. Ensure missing fields are clinically not applicable before proceeding.</span>
            </div>
          )}
        </div>
        <div className="rcn-modal-footer">
          <button className="rcn-modal-btn rcn-modal-btn--cancel" onClick={onCancel} type="button" disabled={submitting}>Cancel</button>
          <button className="rcn-modal-btn rcn-modal-btn--lock" onClick={onConfirm} type="button" disabled={submitting}>
            {submitting ? "Locking…" : <><Lock size={14}/> Yes, Lock It</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Postgres TIMESTAMP (no time zone) columns — e.g. override_unlocked_until —
// serialize to JSON with no 'Z'/offset suffix even though the value is UTC
// (set via datetime.utcnow() on the backend). `new Date("...no suffix...")`
// parses that as LOCAL browser time per the JS spec, not UTC — in IST
// (UTC+5:30) that made a just-created 2-hour override compare as already
// expired. Treat any timestamp with no explicit offset as UTC.
function parseUtcTimestamp(value) {
  if (!value) return null;
  return /[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? new Date(value) : new Date(value + "Z");
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function MetabRenalVascEyeLog() {
  const { enrollmentId } = useParams();
  const navigate         = useNavigate();
  const { markFormCompleted, unmarkFormCompleted } = useFormProgress();
  const { patientData }  = usePatient();
  const { user }         = useAuth();
  const userRole         = user?.role || "site_user";
  const isSuperadmin     = (userRole || "").toLowerCase() === "superadmin";
  const isPI             = (userRole || "").toLowerCase() === "site_pi";
  // Audit trail ("History") is superadmin + site PI only — matches the
  // backend's /audit/ role check in routers/audit.py.
  const canViewAudit     = isSuperadmin || isPI
    || ["project_scientist", "global_scientist", "tech_support"].includes((userRole || "").toLowerCase());

  /* ── UI state ── */
  const [activeDay, setActiveDay]         = useState(1);
  // Paper CRF shows NICU days 1–31
  const [totalDays, setTotalDays]         = useState(31);
  // Day 1 date — Form B date_of_birth only (read-only in UI).
  const [day1Date, setDay1Date] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [completedDays, setCompletedDays] = useState([]);
  const [dayStatuses, setDayStatuses]     = useState({});
  const [dayMeta, setDayMeta]             = useState({});
  const [dischargeDay, setDischargeDay]   = useState(null);
  const [isSaved, setIsSaved]             = useState(false);
  const [dayReloadNonce, setDayReloadNonce] = useState(0);
  const loadedUpdatedAtRef = useRef(null);
  const [isEditing, setIsEditing]         = useState(false);
  const [message, setMessage]             = useState("");
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [loading, setLoading]             = useState(false);
  const [showModal, setShowModal]         = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [savedAt, setSavedAt]             = useState(null);
  const [savedBy, setSavedBy]             = useState("");
  const [submittedAt, setSubmittedAt]     = useState(null);
  const [submittedBy, setSubmittedBy]     = useState("");

  /* ── All-days table view ── */
  const [showTableView, setShowTableView]   = useState(false);
  const [tableViewRows, setTableViewRows]   = useState([]);
  const [tableViewLoading, setTableViewLoading] = useState(false);

  /* ── Per-day "what's missing" popover (shown on partial/late day pills) ──
     Rendered via a portal at fixed viewport coordinates (not nested inside
     the day strip) because .rcn-timeline scrolls horizontally — any
     non-"visible" overflow-x forces overflow-y to compute as "auto" too
     (per the CSS overflow spec), which would silently clip a popover
     taller than the pill row. Portaling to <body> sidesteps that. */
  const [missingPopoverDay, setMissingPopoverDay] = useState(null); // day number or null
  const [missingPopoverPos, setMissingPopoverPos] = useState(null); // { top, left }
  const [missingFieldsCache, setMissingFieldsCache] = useState({}); // { [day]: [{section, labels:[]}] }
  const [missingLoading, setMissingLoading] = useState(false);
  const missingPopoverRef = useRef(null);
  const missingAnchorRef  = useRef(null); // the badge <button> currently open, so scroll/resize can re-measure it

  /* ── Audit trail ── */
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditEntries, setAuditEntries]     = useState([]);
  const [auditLoading, setAuditLoading]     = useState(false);

  /* ── Site-monitor override ── */
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason]       = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideUntil, setOverrideUntil]          = useState(null);

  const [patientInfo, setPatientInfo] = useState({
    enrollmentId: enrollmentId || "",
    babyUid:"",
    babyName:"",
    motherName:"",
    gestationalAge:"",
    gestationSource:"",
    admissionDate:"",
    dischargeDate:"",
    status:"In NICU",
  });

  /* ═══════════════════════════════════════
     SECTION STATES
  ═══════════════════════════════════════ */

  // ⚡ METABOLIC (4.1, items 1-10)
  const [metabData, setMetabData] = useState({
    lowest_glucose:         null, // #1 autofill
    hypoglycemia_episodes:  null, // #2 autofill
    hypoglycemia_rx:        null, // #3 gated
    highest_glucose:        null, // #4 autofill
    insulin:                null, // #5 gated hyper Rx
    metabolic_acidosis:     null, // #6 derived from ph_readings
    metabolic_acidosis_status: null, // #6 "Not Recorded / Not Done"
    sodium_value:           null, // #7 summary
    potassium_value:        null, // #8 summary
    ionized_calcium_value:  null, // #9 summary
    osteopenia_suspected:   null, // #10
    ph_readings:            [blankReading({ ph: "" })],
    sodium_readings:        [blankReading({ value: "" })],
    potassium_readings:     [blankReading({ value: "" })],
    calcium_readings:       [blankReading({ value: "" })],
  });
  // Tracks which of #1/#2/#4 still show the Helper-5 autofill badge (cleared on manual edit).
  const [glucoseAutofilled, setGlucoseAutofilled] = useState({
    lowest_glucose: false,
    hypoglycemia_episodes: false,
    highest_glucose: false,
  });
  const glucoseAutofilledRef = useRef(glucoseAutofilled);
  glucoseAutofilledRef.current = glucoseAutofilled;
  const [glucoseRefreshing, setGlucoseRefreshing] = useState(false);
  const glucoseAutoDoneRef = useRef(null);
  const lastAutoComputedRef = useRef({});
  // Server's current Minimal Monitoring sheet date (GET /today record_date).
  const [mmlSheetDate, setMmlSheetDate] = useState(null);

  // 💧 RENAL (4.2, items 11-14)
  const [renalData, setRenalData] = useState({
    aki_suspected:          null, // #11 Yes/No
    creatinine_value:       null, // #12 string | Not Tested | Awaited
    urine_output_8am_2pm:   null,
    urine_output_8am_2pm_status: null,
    urine_output_2pm_8pm:   null,
    urine_output_2pm_8pm_status: null,
    urine_output_8pm_8am:   null,
    urine_output_8pm_8am_status: null,
    urine_output_total:     null, // derived sum
    dialysis_crrt:          null, // #14
  });

  // 🌡️ THERMOREGULATION (4.3, item 15)
  const [thermoData, setThermoData] = useState({
    axillary_temperature:   null, // #15
  });

  // 📍 LOCATION & OUTCOME (4.6, 4.7)
  const [tailData, setTailData] = useState({
    location:               [], // site-wise: PGIMER AMCC wards; other sites DR/NICU/…
    survived_the_day:       null,
  });

  // 🩺 VASCULAR ACCESS
  const [vascData, setVascData] = useState({
    picc_in_situ:          null,
    uvc_in_situ:           null,
    uac_in_situ:           null,
    peripheral_iv:         null,
    peripheral_arterial:   null,
    extravasation_injury:  null,
    line_complication:     null,
  });

  // 👁️ OPHTHALMOLOGY
  const [eyeData, setEyeData] = useState({
    rop_screening_due:     null,
    rop_screened:          null,
    rop_detected:          null,
    rop_stage:             null, // "1"–"5"
    plus_disease:          null,
    rop_treatment:         null,
  });

  /* ── Visibility flags ── */
  const ropYes      = eyeData.rop_detected        === true;

  /* ── Calendar-based day locking ──
     todayNicuDay = which NICU day is the current *working* day given
     Day 1 Date, using NICU_DAY_GRACE_HOUR so overnight staff still count
     as "today" until that hour. Badges, future-locking, and the default
     tab all use this same number. */
  const todayNicuDay = useNicuWorkingDay(day1Date);

  // Green tick (PI rule 2026-09-26): every NICU day from Day 1 up to
  // yesterday is 100% complete; today may still be in progress. dayMeta holds
  // each day's pct (from the summary on load, updated on every save).
  useEffect(() => {
    const lastDay = helperLastRequiredDay({ todayNicuDay, dischargeDay });
    const pctByDay = Object.fromEntries(
      Object.entries(dayMeta || {}).map(([d, m]) => [d, m?.pct]),
    );
    if (isHelperLogComplete(pctByDay, lastDay)) markFormCompleted("metab_renal_vasc_eye");
    else unmarkFormCompleted("metab_renal_vasc_eye");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayMeta, todayNicuDay, dischargeDay]);

  /** Calendar date for the open NICU day (day1Date + activeDay − 1). */
  const activeDayDate = useMemo(() => {
    if (!day1Date) return null;
    const base = new Date(day1Date + "T00:00:00");
    if (isNaN(base.getTime())) return null;
    base.setDate(base.getDate() + activeDay - 1);
    return toDateOnlyValue(base);
  }, [day1Date, activeDay]);
  const activeDayDateRef = useRef(activeDayDate);
  activeDayDateRef.current = activeDayDate;

  /** Autofill / "Refresh from Daily Monitoring Sheet" only when this NICU day is the
   *  server's current MM sheet — GET /today `record_date` from
   *  `_mml_sheet_date()`, never `new Date()` on the browser. A skewed
   *  client clock must not treat a past day as live-today. */
  const isActiveDayToday = useMemo(() => {
    if (!activeDayDate || !mmlSheetDate) return false;
    return activeDayDate === mmlSheetDate;
  }, [activeDayDate, mmlSheetDate]);

  const isFutureActiveDay = todayNicuDay != null && activeDay > todayNicuDay;
  const isAfterStay = dischargeDay != null && activeDay > dischargeDay;
  // Informational only now — locking is manual (see the Lock button below),
  // so a past calendar date no longer forces a day read-only by itself.
  const isPastActiveDay   = todayNicuDay != null && activeDay < todayNicuDay;
  // Site-monitor override reopens an otherwise-locked day for a limited window.
  const isOverrideActiveDay =
    overrideUntil != null && new Date() < parseUtcTimestamp(overrideUntil);

  useDefaultToWorkingNicuDay(todayNicuDay, enrollmentId, activeDay, setActiveDay, dischargeDay);

  useEffect(() => {
    if (!enrollmentId || activeDay == null) return;
    rememberActiveDay(HELPER_SESSION_KEY_METAB_RENAL_VASC_EYE, enrollmentId, activeDay);
  }, [enrollmentId, activeDay]);

  const isSubmitted     = (dayStatuses[activeDay] || STATUS.EMPTY) === STATUS.SUBMITTED;
  const isFieldEditable =
    // Day 1 Date is mandatory — nurses must set it before any daily field
    // can be filled in, so it's no longer possible to save Day 1 (or any
    // day) data and forget the date. See handleSave for the same guard on
    // the actual save call (independent of `force`).
    !!day1Date &&
    (!isSubmitted || isOverrideActiveDay) &&
    (!isSaved || isEditing) &&
    !isFutureActiveDay &&
    !isAfterStay;
    // NOTE: locking is now manual only (via the explicit "Lock" button +
    // confirmation modal below), so a past day's calendar date passing no
    // longer forces the record read-only on its own — only isSubmitted
    // (i.e. a nurse/site user explicitly clicked Lock and confirmed) does.

  /* ═══════════════════════════════════════
     PROGRESS — hidden fields excluded
  ═══════════════════════════════════════ */
  const ans = v => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);

  // Metabolic: gated #3/#5; #6–#9 answered via derived/summary values from readings
  const hypoEpisodesNum = (() => {
    const v = metabData.hypoglycemia_episodes;
    if (v === null || v === undefined || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  })();
  const hypoRxRequired = hypoEpisodesNum > 0;
  const hyperRxRequired = isNumericHighGlucose(metabData.highest_glucose);
  const extravasationRequired =
    vascData.peripheral_iv === true || vascData.peripheral_arterial === true;
  const ropScreenedYes = eyeData.rop_screened === true;

  const METAB_BASE = [
    "lowest_glucose", "hypoglycemia_episodes",
    ...(hypoRxRequired ? ["hypoglycemia_rx"] : []),
    "highest_glucose",
    ...(hyperRxRequired ? ["insulin"] : []),
    "metabolic_acidosis",
    "sodium_value", "potassium_value", "ionized_calcium_value",
    "osteopenia_suspected",
  ];
  const metabTotal   = METAB_BASE.length;
  const metabAnswered= METAB_BASE.filter(k => {
    if (k === "metabolic_acidosis") {
      return ans(metabData.metabolic_acidosis) || ans(metabData.metabolic_acidosis_status);
    }
    return ans(metabData[k]);
  }).length;

  // Renal: #11 Yes/No, #12 creatinine_value, #13 any urine window, #14
  const RENAL_KEYS = [
    "aki_suspected",
    "creatinine_value",
    "urine_output_total",
    "dialysis_crrt",
  ];
  const urineAnswered =
    ans(renalData.urine_output_8am_2pm) || ans(renalData.urine_output_8am_2pm_status)
    || ans(renalData.urine_output_2pm_8pm) || ans(renalData.urine_output_2pm_8pm_status)
    || ans(renalData.urine_output_8pm_8am) || ans(renalData.urine_output_8pm_8am_status)
    || ans(renalData.urine_output_total);
  const renalTotal = RENAL_KEYS.length;
  const renalAnswered = RENAL_KEYS.filter(k => {
    if (k === "urine_output_total") return urineAnswered;
    return ans(renalData[k]);
  }).length;

  // Thermoregulation: 1 field (item 15)
  const THERMO_KEYS    = ["axillary_temperature"];
  const thermoTotal    = THERMO_KEYS.length;
  const thermoAnswered = THERMO_KEYS.filter(k => ans(thermoData[k])).length;

  // Vascular: #21 gated on #19 or #20 Yes
  const VASC_KEYS = [
    "picc_in_situ", "uvc_in_situ", "uac_in_situ",
    "peripheral_iv", "peripheral_arterial",
    ...(extravasationRequired ? ["extravasation_injury"] : []),
    "line_complication",
  ];
  const vascTotal    = VASC_KEYS.length;
  const vascAnswered = Math.min(VASC_KEYS.filter(k => ans(vascData[k])).length, vascTotal);

  // Eye: #23 and #24 always asked (an eye exam can happen on a day the
  // protocol did not call due, PI 2026-10-20); #25 gated on #24 Yes
  const EYE_BASE = [
    "rop_screening_due",
    "rop_screened",
    ...(ropScreenedYes ? ["rop_detected"] : []),
  ];
  const eyeTotal  = EYE_BASE.length;
  const eyeAnswered = Math.min(EYE_BASE.filter(k => ans(eyeData[k])).length, eyeTotal);

  // Location & Survived the day: 2 fields (4.6, 4.7)
  const TAIL_KEYS    = ["location","survived_the_day"];
  const tailTotal    = TAIL_KEYS.length;
  const tailAnswered = TAIL_KEYS.filter(k => ans(tailData[k])).length;

  const totalAnswered = metabAnswered + renalAnswered + thermoAnswered + vascAnswered + eyeAnswered + tailAnswered;
  const totalFields   = metabTotal + renalTotal + thermoTotal + vascTotal + eyeTotal + tailTotal;
  const completionPct = totalFields > 0 ? Math.min(100, Math.round((totalAnswered / totalFields) * 100)) : 0;
  // completionPct===100 alone isn't enough once a day has been overridden:
  // isSubmitted stays true for the whole override window (the backend only
  // clears it on an actual re-lock), so without the isOverrideActiveDay
  // check here canSubmit was permanently false during any override —
  // the footer always fell to "Save Correction" and the day could never
  // be re-locked. Allow re-locking while the override window is open.
  // "Survived the day": No on the day of death recorded elsewhere (DMS, Form H).
  useEffect(() => {
    if (patientInfo.status === "Died" && activeDay === dischargeDay && tailData.survived_the_day === null) {
      setTailData(p => ({ ...p, survived_the_day: false }));
    }
  }, [patientInfo.status, activeDay, dischargeDay, tailData.survived_the_day]);
  const laterDaysSaved = Object.keys(dayMeta).map(Number).filter(d => d > activeDay).sort((a, b) => a - b);

  const canSubmit     = completionPct === 100 && (!isSubmitted || isOverrideActiveDay);

  /* ── Setters ── */
  const setMetab = (k, v) => {
    if (!isFieldEditable) return;
    setMetabData(p => {
      const next = { ...p, [k]: v };
      if (k === "hypoglycemia_episodes") {
        const n = v === null || v === undefined || v === "" ? 0 : Number(v);
        if (!Number.isFinite(n) || n <= 0) next.hypoglycemia_rx = null;
      }
      if (k === "highest_glucose" && !isNumericHighGlucose(v)) next.insulin = null;
      return next;
    });
  };

  /** Clinician edit of an auto-glucose field: keep the Helper-5 tag only
   *  while the typed value still equals the last computed autofill. */
  const setGlucoseField = (k, v) => {
    setMetab(k, v);
    const last = lastAutoComputedRef.current[k];
    const stillMatches = last !== undefined && String(v) === String(last);
    setGlucoseAutofilled(prev => (
      prev[k] === stillMatches ? prev : { ...prev, [k]: stillMatches }
    ));
  };

  const updateReadingList = (listKey, valueKey, updater) => {
    if (!isFieldEditable) return;
    setMetabData(p => {
      const list = [...(p[listKey] || [])];
      const nextList = updater(list);
      const next = { ...p, [listKey]: nextList };
      if (listKey === "ph_readings") {
        next.metabolic_acidosis = p.metabolic_acidosis_status === STATUS_NOT_DONE
          ? null
          : deriveMetabolicAcidosis(nextList);
      } else if (listKey === "sodium_readings") {
        next.sodium_value = latestReadingSummary(nextList, valueKey);
      } else if (listKey === "potassium_readings") {
        next.potassium_value = latestReadingSummary(nextList, valueKey);
      } else if (listKey === "calcium_readings") {
        next.ionized_calcium_value = latestReadingSummary(nextList, valueKey);
      }
      return next;
    });
  };

  const setReadingField = (listKey, idx, key, value, valueKey = "value") => {
    updateReadingList(listKey, valueKey, list => {
      const next = [...list];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addReading = (listKey, blank, valueKey = "value") => {
    updateReadingList(listKey, valueKey, list => [...(list.length ? list : []), blank]);
  };
  const removeReading = (listKey, idx, valueKey = "value") => {
    updateReadingList(listKey, valueKey, list => {
      if (list.length <= 1) return list;
      const next = [...list];
      next.splice(idx, 1);
      return next;
    });
  };

  /* Sets the sodium/potassium/ionized-calcium summary field directly to a
   * "Result Awaited" / "Not Recorded / Not Done" sentinel string, bypassing
   * the readings-derived summary. The readings list itself is left as-is —
   * adding a real reading afterward will recompute the summary and clear
   * the sentinel automatically (see updateReadingList above). While a
   * sentinel is active, the corresponding ReadingsBlock is disabled so a
   * contradictory reading can't be entered at the same time. */
  const setLabStatus = (summaryKey, status) => {
    if (!isFieldEditable) return;
    setMetabData(p => ({ ...p, [summaryKey]: p[summaryKey] === status ? null : status }));
  };
  /** Field #6 sidecar: "Not Recorded / Not Done" lives in
   *  metabolic_acidosis_status (same pattern as urine_output_*_status),
   *  not in the derived boolean. Turning the sentinel on clears pH
   *  readings and the Yes/No; turning it off restores a blank editable
   *  readings list. StatusToggleGroup always fires the sentinel value,
   *  so this handler must toggle — same as setLabStatus / NumRow. */
  const setAcidosisStatus = (status) => {
    if (!isFieldEditable) return;
    setMetabData(p => {
      if (p.metabolic_acidosis_status === status) {
        return { ...p, metabolic_acidosis_status: null };
      }
      return {
        ...p,
        metabolic_acidosis_status: status,
        metabolic_acidosis: null,
        ph_readings: [blankReading({ ph: "" })],
      };
    });
  };
  const labStatus = summaryKey => {
    const v = metabData[summaryKey];
    return v === STATUS_AWAITED || v === STATUS_NOT_DONE ? v : null;
  };

  const setRenal = (k, v) => {
    if (!isFieldEditable) return;
    setRenalData(p => {
      const next = { ...p, [k]: v };
      if (
        k === "urine_output_8am_2pm"
        || k === "urine_output_2pm_8pm"
        || k === "urine_output_8pm_8am"
      ) {
        next.urine_output_total = computeUrineTotal(
          k === "urine_output_8am_2pm" ? v : p.urine_output_8am_2pm,
          k === "urine_output_2pm_8pm" ? v : p.urine_output_2pm_8pm,
          k === "urine_output_8pm_8am" ? v : p.urine_output_8pm_8am,
        );
      }
      return next;
    });
  };
  const setThermo= (k, v) => isFieldEditable && setThermoData(p => ({ ...p, [k]: v }));
  const setVasc  = (k, v) => {
    if (!isFieldEditable) return;
    setVascData(p => {
      const next = { ...p, [k]: v };
      const iv = k === "peripheral_iv" ? v : next.peripheral_iv;
      const art = k === "peripheral_arterial" ? v : next.peripheral_arterial;
      if (iv !== true && art !== true) next.extravasation_injury = null;
      return next;
    });
  };
  const setEye = (k, v) => {
    if (!isFieldEditable) return;
    setEyeData(p => {
      const next = { ...p, [k]: v };
      if (k === "rop_screened" && v !== true) {
        next.rop_detected = null;
        next.rop_stage = null;
        next.plus_disease = null;
        next.rop_treatment = null;
      }
      if (k === "rop_detected" && v !== true) {
        next.rop_stage = null;
        next.plus_disease = null;
        next.rop_treatment = null;
      }
      return next;
    });
  };
  const setTail  = (k, v) => isFieldEditable && setTailData(p => ({ ...p, [k]: v }));

  /* ── Glucose autofill from Helper Form 5 (this NICU day's MM sheet) ── */
  const metabDataRef = useRef(metabData);
  metabDataRef.current = metabData;

  // DMS values filled into a saved, unlocked day while it is in view mode are
  // saved quietly (PI 2026-11-04). Before view mode (86b5ded) the autofill
  // opened edit mode and autosave stored them; without this the screen
  // showed DMS values the stored day did not have.
  const dmsChangedRef = useRef(false);
  const [dmsSaveTick, setDmsSaveTick] = useState(0);
  const noteDmsChange = () => { dmsChangedRef.current = true; setDmsSaveTick(n => n + 1); };

  const applyGlucoseAutofill = async ({ force = false, seed = null } = {}) => {
    const viewedDate = activeDayDate;
    if (!enrollmentId || !viewedDate) return false;
    try {
      // Fetch the MM sheet for THIS NICU day's calendar date — never GET /today.
      const res = await getMmlSheet(`/minimal-monitoring/${enrollmentId}/on/${viewedDate}`);
      if (activeDayDateRef.current !== viewedDate) return false;
      const data = res?.data || {};
      // No MM row for this calendar date — do not invent "Not Tested".
      if (data.id == null) return false;
      // Guard of record: the MM row's record_date must be the day being viewed.
      if (!data.record_date || data.record_date !== viewedDate) return false;

      const readings = parseMetAGlucoseReadings(data);
      const computed = computeGlucoseAutofill(readings);
      const base = seed || metabDataRef.current;
      const next = { ...base };
      const afNext = { ...glucoseAutofilledRef.current };
      let anyChanged = false;
      for (const key of ["lowest_glucose", "hypoglycemia_episodes", "highest_glucose"]) {
        const lastComputed = lastAutoComputedRef.current[key];
        const stillMatchesLastAutoFill = lastComputed !== undefined
          && String(base[key]) === String(lastComputed);
        const sync = mmlSyncGlucoseFieldFromMml({
          current: base[key],
          wasAutofilled: !!glucoseAutofilledRef.current[key],
          stillMatchesLastAuto: stillMatchesLastAutoFill,
          force,
          fieldKey: key,
          readings,
          computedValue: String(computed[key]),
          looksSourced: glucoseFieldLooksMmlSourced,
        });
        if (sync.changed) {
          next[key] = sync.nextValue === "" ? null : sync.nextValue;
          afNext[key] = sync.autofilled;
          lastAutoComputedRef.current[key] = next[key];
          anyChanged = true;
        } else if (sync.autofilled) {
          afNext[key] = true;
        }
      }
      if (!anyChanged) return false;

      const ep = Number(next.hypoglycemia_episodes);
      if (!Number.isFinite(ep) || ep <= 0) next.hypoglycemia_rx = null;
      if (!isNumericHighGlucose(next.highest_glucose)) next.insulin = null;

      if (activeDayDateRef.current !== viewedDate) return false;
      setMetabData(next);
      setGlucoseAutofilled(afNext);
      noteDmsChange();
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleRefreshGlucoseFromHelper5 = async () => {
    if (!isFieldEditable || !activeDayDate) return;
    setGlucoseRefreshing(true);
    try {
      const ok = await applyGlucoseAutofill({ force: true });
      setMessage(ok
        ? "✅ Glucose fields refreshed from Daily Monitoring Sheet"
        : "⚠️ No matching Daily Monitoring Sheet for this day");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setGlucoseRefreshing(false);
    }
  };

  /* ── Load patient info ── */
  useEffect(() => {
    if (!enrollmentId) return;
    const load = async () => {
      let timelineDisch = null;
      let timelineDob = "";
      try {
        const res = await api.get(`/birth-resuscitation/${enrollmentId}`);
        const b = res?.data || {};
        if (b.date_of_birth) {
          setDay1Date(normalizeHelperDob(b.date_of_birth));
          setBirthTime(b.time_of_birth ? String(b.time_of_birth).slice(0, 5) : "");
        }

        // Load gestation with NBS correction check (same logic as FiO2 form)
        let gestWeeks = b?.gestation_weeks;
        let gestDays = b?.gestation_days ?? 0;
        let gestSource = b?.gestation_source || "Form B";

        try {
          const dRes = await api.get(`/postnatal-day1/${enrollmentId}`);
          const d = dRes?.data || {};
          const originalWeeks = b?.original_gestation_weeks ?? b?.gestation_weeks;
          const originalDays = b?.original_gestation_days ?? b?.gestation_days ?? 0;
          const originalTotal = totalGestationDays(originalWeeks, originalDays);
          const nbsTotal = totalGestationDays(d?.gestation_weeks, d?.gestation_days);
          const useNbs = d?.ga_method === "NBS" && nbsTotal !== null && (
            originalTotal === null || Math.abs(nbsTotal - originalTotal) > 14
          );
          if (useNbs) {
            gestWeeks = d.gestation_weeks;
            gestDays = d.gestation_days ?? 0;
            gestSource = "Form D NBS";
          }
        } catch (_) {
          // Form D not available or no NBS correction — use Form B values
        }

        const ga = formatGestation(gestWeeks, gestDays);

        const dob = normalizeHelperDob(b.date_of_birth);
        const dischDay = nicuDayForCalendarYmd(dob, b.discharge_date);
        if (dischDay != null) setDischargeDay(dischDay);
        timelineDisch = dischDay;
        timelineDob = dob;

        setPatientInfo(prev => ({
          ...prev, enrollmentId,
          babyUid: b.baby_uid || "", 
          gestationalAge: ga,
          gestationSource: gestSource,
          admissionDate: dob,
          dischargeDate: b.discharge_date || "",
          status: b.died ? "Died" : b.discharge_date ? "Discharged" : "In NICU",
        }));
        setTotalDays(helperDayStripLength({
          dischargeDay: timelineDisch,
          todayNicuDay: nicuDayNumberFromDay1(timelineDob),
        }));
      } catch (_) {}

      // Load PII — mother_first_name, mother_surname, baby_name
      try {
        const piiRes = await api.get(`/pii/enrollment/${enrollmentId}`);
        const p = piiRes?.data || {};
        const motherName = `${p.mother_first_name || ""} ${p.mother_surname || ""}`.trim();
        setPatientInfo(prev => ({
          ...prev,
          motherName: motherName || "",
          babyName:   p.baby_name || "",
        }));
      } catch (_) {}

      // Load summary
      try {
        const summRes = await api.get(`/metab-renal-vasc-eye/${enrollmentId}/summary`);
        const sums = summRes?.data || [];
        const newSt = {}, newMeta = {};
        sums.forEach(s => {
          newSt[s.nicu_day]   = helperDayDisplayStatus(
            s.submission_status || STATUS.DRAFT,
            s.completion_pct || 0,
          );
          newMeta[s.nicu_day] = { pct: s.completion_pct || 0, savedAt: s.saved_at };
        });
        setDayStatuses(newSt); setDayMeta(newMeta);
        const savedMax = sums.reduce((m, s) => Math.max(m, s.nicu_day || 0), 0);
        setTotalDays((prev) => Math.max(
          prev,
          helperDayStripLength({
            dischargeDay: timelineDisch,
            todayNicuDay: nicuDayNumberFromDay1(timelineDob),
            savedMaxDay: savedMax,
          }),
        ));
      } catch (_) {}
    };
    load();
  }, [enrollmentId]);

  // Extend strip through today's working NICU day (no manual "+ DAY" for a new calendar day).
  useEffect(() => {
    if (!day1Date) return;
    setTotalDays((prev) => Math.max(
      prev,
      helperDayStripLength({ dischargeDay, todayNicuDay }),
    ));
  }, [day1Date, dischargeDay, todayNicuDay]);

  // Server MM "today" (GET /today record_date) — drives isActiveDayToday.
  useEffect(() => {
    if (!enrollmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMmlSheet(`/minimal-monitoring/${enrollmentId}/today`, {
          params: { boundary_hour: NICU_DAY_GRACE_HOUR },
        });
        const rd = res?.data?.record_date;
        if (!cancelled && rd) setMmlSheetDate(rd);
      } catch (_) { /* DMS optional */ }
    })();
    return () => { cancelled = true; };
  }, [enrollmentId]);

  /* ── Load day data ── */
  useEffect(() => {
    if (!enrollmentId) return;
    let cancelled = false;
    const emptyMetab = {
      lowest_glucose:null,hypoglycemia_episodes:null,hypoglycemia_rx:null,highest_glucose:null,
      insulin:null,metabolic_acidosis:null,metabolic_acidosis_status:null,sodium_value:null,potassium_value:null,
      ionized_calcium_value:null,osteopenia_suspected:null,
      ph_readings:[blankReading({ ph: "" })],
      sodium_readings:[blankReading({ value: "" })],
      potassium_readings:[blankReading({ value: "" })],
      calcium_readings:[blankReading({ value: "" })],
    };
    const loadDay = async () => {
      setLoading(true);
      glucoseAutoDoneRef.current = null;
      lastAutoComputedRef.current = {};
      setGlucoseAutofilled({
        lowest_glucose: false,
        hypoglycemia_episodes: false,
        highest_glucose: false,
      });
      let loadedMetab = null;
      try {
        const res = await api.get(`/metab-renal-vasc-eye/${enrollmentId}/${activeDay}`);
        if (cancelled) return;
        const d = res?.data || {};
        if (d && Object.keys(d).length > 0) {
          const aki = migrateAkiFromLegacy(d);
          const phReadings = parseReadings(d.ph_readings_json) || [blankReading({ ph: "" })];
          const naReadings = parseReadings(d.sodium_readings_json) || [blankReading({ value: "" })];
          const kReadings = parseReadings(d.potassium_readings_json) || [blankReading({ value: "" })];
          const caReadings = parseReadings(d.calcium_readings_json) || [blankReading({ value: "" })];
          const creatVal = d.creatinine_value
            ?? (d.creatinine != null && d.creatinine !== "" ? String(d.creatinine) : null);
          const acidosisStatus = d.metabolic_acidosis_status ?? null;
          const acidosisNotDone = acidosisStatus === STATUS_NOT_DONE;
          loadedMetab = {
            lowest_glucose:         d.lowest_glucose         ?? null,
            hypoglycemia_episodes:  d.hypoglycemia_episodes  ?? null,
            hypoglycemia_rx:        d.hypoglycemia_rx        ?? null,
            highest_glucose:        d.highest_glucose        ?? null,
            insulin:                d.insulin                ?? null,
            metabolic_acidosis:     acidosisNotDone ? null : (d.metabolic_acidosis ?? deriveMetabolicAcidosis(phReadings)),
            metabolic_acidosis_status: acidosisStatus,
            sodium_value:           d.sodium_value ?? latestReadingSummary(naReadings),
            potassium_value:        d.potassium_value ?? latestReadingSummary(kReadings),
            ionized_calcium_value:  d.ionized_calcium_value ?? latestReadingSummary(caReadings),
            osteopenia_suspected:   d.osteopenia_suspected   ?? null,
            ph_readings:            acidosisNotDone ? [blankReading({ ph: "" })] : phReadings,
            sodium_readings:        naReadings,
            potassium_readings:     kReadings,
            calcium_readings:       caReadings,
          };
          setMetabData(loadedMetab);
          setRenalData({
            aki_suspected:          aki.aki_suspected,
            creatinine_value:       creatVal,
            urine_output_8am_2pm:   d.urine_output_8am_2pm ?? null,
            urine_output_8am_2pm_status: d.urine_output_8am_2pm_status ?? null,
            urine_output_2pm_8pm:   d.urine_output_2pm_8pm ?? null,
            urine_output_2pm_8pm_status: d.urine_output_2pm_8pm_status ?? null,
            urine_output_8pm_8am:   d.urine_output_8pm_8am ?? null,
            urine_output_8pm_8am_status: d.urine_output_8pm_8am_status ?? null,
            urine_output_total:     d.urine_output_total
              ?? computeUrineTotal(d.urine_output_8am_2pm, d.urine_output_2pm_8pm, d.urine_output_8pm_8am),
            dialysis_crrt:          d.dialysis_crrt      ?? null,
          });
          setThermoData({
            axillary_temperature: d.axillary_temperature ?? null,
          });
          setVascData({
            picc_in_situ:         d.picc_in_situ         ?? null,
            uvc_in_situ:          d.uvc_in_situ          ?? null,
            uac_in_situ:          d.uac_in_situ          ?? null,
            peripheral_iv:        d.peripheral_iv        ?? null,
            peripheral_arterial:  d.peripheral_arterial  ?? null,
            extravasation_injury: d.extravasation_injury ?? null,
            line_complication:    d.line_complication    ?? null,
          });
          setEyeData({
            rop_screening_due: d.rop_screening_due ?? null,
            rop_screened:      d.rop_screened      ?? null,
            rop_detected:      d.rop_detected      ?? null,
            rop_stage:         d.rop_stage         || null,
            plus_disease:      d.plus_disease      ?? null,
            rop_treatment:     d.rop_treatment     ?? null,
          });
          setTailData({
            location:          normalizeHelperLocations(stringToList(d.location)),
            survived_the_day:  d.survived_the_day  ?? null,
          });
          const st = d.submission_status || STATUS.DRAFT;
          setDayStatuses(prev => ({
            ...prev,
            [activeDay]: helperDayDisplayStatus(st, d.completion_pct),
          }));
          setSavedAt(d.saved_at||null); setSavedBy(d.saved_by||"");
          setSubmittedAt(d.submitted_at||null); setSubmittedBy(d.submitted_by||"");
          setOverrideUntil(d.override_unlocked_until || null);
          loadedUpdatedAtRef.current = d.updated_at || null;
          setIsSaved(true);
          // Saved unlocked days open in view mode so Edit is available
          // until Lock. Only a live override should auto-enter edit.
          const overrideStillActive = !!d.override_unlocked_until && parseUtcTimestamp(d.override_unlocked_until) > new Date();
          setIsEditing(st === STATUS.SUBMITTED && overrideStillActive);
          if (!completedDays.includes(activeDay))
            setCompletedDays(prev => [...prev, activeDay]);
        } else {
          resetFormState();
          loadedMetab = emptyMetab;
        }
      } catch (err) {
        if (cancelled) return;
        // Always clear — never leave previous day's values in the form.
        resetFormState();
        loadedMetab = emptyMetab;
        if (err?.response?.status !== 404) {
          setMessage("❌ Could not load Day " + activeDay + " — save disabled until reload");
          setTimeout(() => setMessage(""), 5000);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      if (!cancelled && loadedMetab) {
        await applyGlucoseAutofill({ seed: loadedMetab });
      }
    };
    loadDay();
    return () => { cancelled = true; };
  }, [enrollmentId, activeDay, day1Date, dayReloadNonce]);

  /* Re-aggregate DMS glucose while today's day is open and editable.
     Blank or still-auto values update; clinician-typed values are left alone.
     Manual "Refresh from Daily Monitoring Sheet" still force-overwrites. */
  useEffect(() => {
    if (!enrollmentId || !isActiveDayToday || !isFieldEditable) return;
    const interval = setInterval(() => {
      if (!document.hidden) applyGlucoseAutofill({ force: false });
    }, 60000); // every 60s; hidden tabs skip (focus/visibility catches up)
    return () => clearInterval(interval);
  }, [enrollmentId, activeDay, isActiveDayToday, isFieldEditable]);

  useEffect(() => {
    if (!enrollmentId || !isActiveDayToday || !isFieldEditable) return;
    const onFocus = () => applyGlucoseAutofill({ force: false });
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enrollmentId, activeDay, isActiveDayToday, isFieldEditable]);

  const resetFormState = () => {
    loadedUpdatedAtRef.current = null;
    setMetabData({
      lowest_glucose:null,hypoglycemia_episodes:null,hypoglycemia_rx:null,highest_glucose:null,
      insulin:null,metabolic_acidosis:null,metabolic_acidosis_status:null,sodium_value:null,potassium_value:null,
      ionized_calcium_value:null,osteopenia_suspected:null,
      ph_readings:[blankReading({ ph: "" })],
      sodium_readings:[blankReading({ value: "" })],
      potassium_readings:[blankReading({ value: "" })],
      calcium_readings:[blankReading({ value: "" })],
    });
    setGlucoseAutofilled({
      lowest_glucose: false,
      hypoglycemia_episodes: false,
      highest_glucose: false,
    });
    setRenalData({
      aki_suspected:null, creatinine_value:null,
      urine_output_8am_2pm:null, urine_output_8am_2pm_status:null,
      urine_output_2pm_8pm:null, urine_output_2pm_8pm_status:null,
      urine_output_8pm_8am:null, urine_output_8pm_8am_status:null,
      urine_output_total:null, dialysis_crrt:null,
    });
    setThermoData({ axillary_temperature:null });
    setVascData({ picc_in_situ:null,uvc_in_situ:null,uac_in_situ:null,peripheral_iv:null,
      peripheral_arterial:null,extravasation_injury:null,line_complication:null });
    setEyeData({ rop_screening_due:null,rop_screened:null,rop_detected:null,rop_stage:null,plus_disease:null,rop_treatment:null });
    setTailData({ location:[],survived_the_day:null });
    setIsSaved(false); setIsEditing(false);
    setSavedAt(null); setSavedBy(""); setSubmittedAt(null); setSubmittedBy("");
    setOverrideUntil(null);
    setDayStatuses(prev => ({ ...prev, [activeDay]: STATUS.EMPTY }));
  };

  const buildPayload = (now) => {
    const acidosisNotDone = metabData.metabolic_acidosis_status === STATUS_NOT_DONE;
    const phReadings = acidosisNotDone
      ? [blankReading({ ph: "" })]
      : (metabData.ph_readings || []);
    const naReadings = metabData.sodium_readings || [];
    const kReadings = metabData.potassium_readings || [];
    const caReadings = metabData.calcium_readings || [];
    const acidosis = acidosisNotDone ? null : deriveMetabolicAcidosis(phReadings);
    const naSum = isStatusSentinel(metabData.sodium_value) ? metabData.sodium_value : latestReadingSummary(naReadings);
    const kSum = isStatusSentinel(metabData.potassium_value) ? metabData.potassium_value : latestReadingSummary(kReadings);
    const caSum = isStatusSentinel(metabData.ionized_calcium_value) ? metabData.ionized_calcium_value : latestReadingSummary(caReadings);
    const urineTotal = computeUrineTotal(
      renalData.urine_output_8am_2pm,
      renalData.urine_output_2pm_8pm,
      renalData.urine_output_8pm_8am,
    );
    const creatVal = renalData.creatinine_value;
    const creatNum = (() => {
      if (creatVal == null || creatVal === "" || isStatusSentinel(creatVal) || creatVal === "Not Tested" || creatVal === "Awaited") return null;
      const n = Number(creatVal);
      return Number.isFinite(n) ? n : null;
    })();
    const {
      ph_readings, sodium_readings, potassium_readings, calcium_readings,
      ...metabFlat
    } = metabData;
    return {
      enrollment_id: enrollmentId,
      nicu_day: activeDay,
      ...metabFlat,
      metabolic_acidosis: acidosis,
      metabolic_acidosis_status: metabData.metabolic_acidosis_status ?? null,
      sodium_value: naSum,
      potassium_value: kSum,
      ionized_calcium_value: caSum,
      ph_readings_json: JSON.stringify(phReadings),
      sodium_readings_json: JSON.stringify(naReadings),
      potassium_readings_json: JSON.stringify(kReadings),
      calcium_readings_json: JSON.stringify(caReadings),
      aki_suspected: renalData.aki_suspected,
      creatinine_value: creatVal,
      creatinine: creatNum,
      urine_output_8am_2pm: renalData.urine_output_8am_2pm,
      urine_output_8am_2pm_status: renalData.urine_output_8am_2pm_status,
      urine_output_2pm_8pm: renalData.urine_output_2pm_8pm,
      urine_output_2pm_8pm_status: renalData.urine_output_2pm_8pm_status,
      urine_output_8pm_8am: renalData.urine_output_8pm_8am,
      urine_output_8pm_8am_status: renalData.urine_output_8pm_8am_status,
      urine_output_total: urineTotal,
      dialysis_crrt: renalData.dialysis_crrt,
      ...thermoData,
      ...vascData,
      ...eyeData,
      ...tailData,
      location: listToString(tailData.location),
      submission_status: helperDaySaveStatus(completionPct),
      saved_at: now,
      saved_by: user?.name || user?.username || "Nurse",
    };
  };
  /* ── Save ── */
  const handleSave = async ({ force = false, silent = false } = {}) => {
    if (!enrollmentId) return silent;
    if (!day1Date) {
      if (!silent) setMessage("⚠️ Day 1 Date is missing — record Date of Birth in Form B first");
      return false;
    }
    if (isAfterStay) {
      if (!silent) {
        const what = patientInfo.status === "Died" ? "died" : "left the hospital";
        setMessage(`This baby ${what} on Day ${dischargeDay}: no entries after that day.`);
      }
      return false;
    }
    // Silent autosave stays in edit mode only. Explicit Save / Save for Later
    // must still work from view mode so Draft stays available before Lock.
    if (!force && !isFieldEditable && silent) return silent;
    if (isSubmitted && !isOverrideActiveDay) return silent;
    if (isFutureActiveDay) return silent;
    const now = new Date().toISOString();
    const payload = buildPayload(now);
    if (!force && !isSaved && isBlankHelperDay(payload)) {
      if (!silent) {
        setMessage(BLANK_HELPER_DAY_CLIENT_MESSAGE);
        setTimeout(() => setMessage(""), 3000);
      }
      return silent;
    }
    try {
      const staleCfg = expectedUpdatedAtConfig(loadedUpdatedAtRef.current);
      const res = isSaved
        ? await api.put(`/metab-renal-vasc-eye/${enrollmentId}/${activeDay}`, payload, staleCfg)
        : await api.post("/metab-renal-vasc-eye/", payload, staleCfg);
      // Sidebar tick: see the dayMeta effect (all days to yesterday at 100%).
      loadedUpdatedAtRef.current = res?.data?.updated_at || loadedUpdatedAtRef.current;
      setIsSaved(true);
      if (!silent) setIsEditing(false);
      setSavedAt(now); setSavedBy(user?.name || user?.username || "Nurse");
      const newSt = completionPct===100 ? STATUS.COMPLETE : STATUS.DRAFT;
      setDayStatuses(prev => ({ ...prev, [activeDay]: newSt }));
      setDayMeta(prev => ({ ...prev, [activeDay]: { pct: completionPct, savedAt: now } }));
      if (!completedDays.includes(activeDay))
        setCompletedDays(prev => [...prev, activeDay]);
      if (!silent) {
        setMessage("✅ Day " + activeDay + " saved successfully");
        setShowSaveSuccess(true);
        setTimeout(() => setMessage(""), 3000);
      }
      return true;
    } catch (err) {
      if (isStaleWrite(err)) {
        setMessage(STALE_WRITE_MESSAGE);
        setDayReloadNonce(n => n + 1);
        return false;
      }
      if (isBlankHelperDayRefusal(err)) {
        if (!silent) {
          setMessage(BLANK_HELPER_DAY_CLIENT_MESSAGE);
          setTimeout(() => setMessage(""), 3000);
        }
        return silent;
      }
      if (!silent) setMessage("❌ " + apiDetailMessage(err));
      return false;
    }
  };

  useEffect(() => {
    if (!dmsChangedRef.current) return;
    dmsChangedRef.current = false;
    if (!isSaved || isEditing) return;   // new day / edit mode: saved as before
    handleSave({ force: true, silent: true });
  }, [dmsSaveTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchActiveDay = async (d) => {
    if (d === activeDay) return;
    if (isFieldEditable) {
      try {
        const ok = await handleSave({ silent: true });
        if (ok === false) return;
      } catch (err) {
        console.error("Save before day change failed:", err);
        return;
      }
    }
    setMessage("");
    setActiveDay(d);
  };

  const handlePrevious = async () => {
    if (isFieldEditable) {
      try { await handleSave({ silent: true }); } catch (err) { console.error("Save before back failed:", err); }
    }
    navigate(`/infect-gi-hema-log/${enrollmentId}`);
  };

  useRegisterActiveFormSession(() => isFieldEditable, () => {
    // A navigation-triggered flush must not create a phantom blank draft —
    // that silently marks the day non-empty and locks Day 1 Date before the
    // user has entered anything. Only auto-save when something real exists.
    return handleSave({ silent: true });
  });

  /* ── Submit ── */
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Always save fresh state before locking — `isSaved` only means "this
      // record has been saved at least once," not "nothing has changed
      // since." Skipping the save here silently discarded edits made after
      // a prior save whenever the day reached 100% (confirmed 2026-08-23 on
      // enrollment 01-A-456: Hypoglycemia Rx + Ionized Calcium were entered,
      // completion hit 100%, the UI shows only a Submit button at that
      // point — no separate Save — and the submit locked the stale
      // pre-edit data because isSaved was already true from an earlier
      // save).
      await handleSave({ force: true });
      const now = new Date().toISOString();
      await api.patch(`/metab-renal-vasc-eye/${enrollmentId}/${activeDay}/submit`, {
        submission_status: STATUS.SUBMITTED,
        submitted_at: now,
        submitted_by: user?.name || user?.username || "Site User",
      });
      setDayStatuses(prev => ({ ...prev, [activeDay]: STATUS.SUBMITTED }));
      setSubmittedAt(now); setSubmittedBy(user?.name || user?.username || "Site User");
      // Locking now (even mid-override) ends the override immediately on
      // the backend — mirror that here so the badge/buttons update without
      // needing a refresh.
      setOverrideUntil(null);
      setShowModal(false);
      setMessage("🔒 Day " + activeDay + " submitted and locked");
      setTimeout(() => setMessage(""), 5000);
    } catch (_) { setMessage("❌ Submission failed"); setShowModal(false); }
    finally { setSubmitting(false); }
  };

  /* ── Next Form (save before navigate) ── */
  const handleNext = async () => {
    // Same phantom-blank-draft guard as handlePrevious / the autosave
    // session flush — clicking Next on an untouched day must not silently
    // POST an empty record and tick the sidebar as complete.
    if (isFieldEditable) {
      try { await handleSave({ silent: true }); } catch (err) { console.error("Save before next failed:", err); }
    }
    navigate(`/form-f/${enrollmentId}`);
  };

  /* ── Audit trail — superadmin + site PI only, scoped to the active day ── */
  const fetchAuditHistory = async () => {
    setShowAuditModal(true);
    setAuditLoading(true);
    try {
      const res = await api.get("/audit/", {
        params: {
          table_name: "metab_renal_vasc_eye_day_logs",
          enrollment_id: enrollmentId,
          limit: 200,
        },
      });
      const entries = (res?.data || []).filter(e => {
        const day = e.new_values?.nicu_day ?? e.old_values?.nicu_day;
        return day === activeDay;
      });
      setAuditEntries(entries);
    } catch (_) {
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const days = Array.from({ length: totalDays }, (_, i) => i + 1);
  // Past days with genuinely no data at all — surfaced as a "missed" warning.
  const missedDays = days.filter(d =>
    todayNicuDay != null && d < todayNicuDay &&
    (dayStatuses[d] || STATUS.EMPTY) === STATUS.EMPTY
  );

  // Fetches full clinical data for every day that has something saved
  // (status != EMPTY), for the "Table View" overview. Reuses the same
  // per-day endpoint the form itself already uses to load a single day —
  // no new backend endpoint needed. Same pattern as Helper Forms 1 & 3.
  const loadTableViewData = async () => {
    setShowTableView(true);
    setTableViewLoading(true);
    try {
      const filledDays = days.filter(d => (dayStatuses[d] || STATUS.EMPTY) !== STATUS.EMPTY);
      const results = await Promise.all(
        filledDays.map(d =>
          api.get(`/metab-renal-vasc-eye/${enrollmentId}/${d}`)
            .then(res => ({ day: d, data: res?.data || null }))
            .catch(() => ({ day: d, data: null }))
        )
      );
      results.sort((a, b) => a.day - b.day);
      setTableViewRows(results.filter(r => r.data));
    } catch (err) {
      console.error("Table view load failed:", err);
    } finally {
      setTableViewLoading(false);
    }
  };

  // Computes a popover position anchored under a badge button, clamped so
  // it never runs off the left/right edges, and flipped above the badge
  // when there isn't enough room below (e.g. a day near the bottom of the
  // screen). POPOVER_MAX_H is a conservative estimate — good enough for
  // flip decisions since a slight misjudgement just means less padding.
  const POPOVER_MAX_H = 260;
  const computeMissingPopoverPos = (anchorEl) => {
    const rect = anchorEl.getBoundingClientRect();
    const left = Math.min(Math.max(rect.left + rect.width / 2, 118), window.innerWidth - 118);
    const roomBelow = window.innerHeight - rect.bottom;
    const top = roomBelow < POPOVER_MAX_H && rect.top > POPOVER_MAX_H
      ? Math.max(8, rect.top - POPOVER_MAX_H - 8)
      : rect.bottom + 8;
    return { top, left };
  };

  // Opens/closes the "what's missing" popover for a day pill. Fetches and
  // caches that day's saved record on first open — cheap, since it only
  // runs for the day the user actually clicks into, not all of them.
  const handleToggleMissing = async (day, e) => {
    e.stopPropagation();
    if (missingPopoverDay === day) { setMissingPopoverDay(null); return; }
    missingAnchorRef.current = e.currentTarget;
    setMissingPopoverPos(computeMissingPopoverPos(e.currentTarget));
    setMissingPopoverDay(day);
    if (missingFieldsCache[day]) return;
    setMissingLoading(true);
    try {
      const res = await api.get(`/metab-renal-vasc-eye/${enrollmentId}/${day}`);
      setMissingFieldsCache(prev => ({ ...prev, [day]: computeMissingFields(res?.data) }));
    } catch {
      setMissingFieldsCache(prev => ({ ...prev, [day]: [] }));
    } finally {
      setMissingLoading(false);
    }
  };

  // Keep the popover glued to its badge while the page (or the day strip)
  // scrolls, instead of just closing it — re-measures the anchor button's
  // live position on every scroll/resize tick. Still closes on an outside
  // click, and on scroll if the anchor has been scrolled out of view
  // entirely (nothing sensible to point at anymore).
  useEffect(() => {
    if (missingPopoverDay === null) return;
    const handlePointerDown = (e) => {
      if (
        missingPopoverRef.current && !missingPopoverRef.current.contains(e.target) &&
        missingAnchorRef.current && !missingAnchorRef.current.contains(e.target)
      ) {
        setMissingPopoverDay(null);
      }
    };
    const reposition = () => {
      const anchor = missingAnchorRef.current;
      if (!anchor || !anchor.isConnected) { setMissingPopoverDay(null); return; }
      const rect = anchor.getBoundingClientRect();
      const offscreen = rect.bottom < 0 || rect.top > window.innerHeight ||
        rect.right < 0 || rect.left > window.innerWidth;
      if (offscreen) { setMissingPopoverDay(null); return; }
      setMissingPopoverPos(computeMissingPopoverPos(anchor));
    };
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [missingPopoverDay]);

  /* ═══════════════════════════════════════ RENDER ═══════════════════════════════════════ */
  return (
    <>
      {/* Unlocked days are always editable (since 7f2068d), so an "editing"
          banner on every saved day carried no information. Only warn when
          a locked day has been reopened via Override & Unlock. */}
      {isSaved && isOverrideActiveDay && (
        <div className="editing-mode-banner">
          <span className="editing-mode-dot" />
          Day {activeDay} reopened by override — you are correcting a locked day. Lock it again when done.
        </div>
      )}

      <div className={`rcn-page${isSaved && !isEditing ? " rcn-readonly" : ""}`}>

        {/* ══ PATIENT INFO HEADER ══ */}
        <div className="rcn-patient-header">
          <div className="rcn-patient-header-title">
            <div className="rcn-patient-header-badge">HELPER FORM 5</div>
            <h2 className="rcn-patient-header-form-name">Metab-Renal-Vasc-Eye</h2>
            <p className="rcn-patient-header-subtitle">
              NICU Day-by-Day Structured Assessment
            </p>
          </div>
          <div className="rcn-patient-cards">
            <div className="rcn-pcard rcn-pcard--blue">
              <span className="rcn-pcard-icon">🪪</span>
              <div className="rcn-pcard-body">
                <span className="rcn-pcard-label">Enrolment ID</span>
                <span className="rcn-pcard-value" title={patientInfo.enrollmentId || ""}>
                  {patientInfo.enrollmentId || "—"}
                </span>
              </div>
            </div>
            <div className="rcn-pcard rcn-pcard--teal">
              <span className="rcn-pcard-icon">🧬</span>
              <div className="rcn-pcard-body">
                <span className="rcn-pcard-label">
                  Gestation{patientInfo.gestationSource === "Form D NBS" ? " (NBS)" : ""}
                </span>
                <span className="rcn-pcard-value" title={patientInfo.gestationalAge || ""}>
                  {patientInfo.gestationalAge || "—"}
                </span>
              </div>
            </div>
            <div className="rcn-pcard rcn-pcard--amber">
              <span className="rcn-pcard-icon">🏷️</span>
              <div className="rcn-pcard-body">
                <span className="rcn-pcard-label">Baby UID</span>
                <span className="rcn-pcard-value" title={patientInfo.babyUid || ""}>
                  {patientInfo.babyUid || "—"}
                </span>
              </div>
            </div>
            <div className="rcn-pcard rcn-pcard--rose">
              <span className="rcn-pcard-icon">👶</span>
              <div className="rcn-pcard-body">
                <span className="rcn-pcard-label">B/O</span>
                <span className="rcn-pcard-value rcn-pcard-value--cap" title={String(patientInfo.babyName || "").replace(/^baby of\s+/i, "").replace(/^b\/o\s+/i, "").trim()}>
                  {String(patientInfo.babyName || "").replace(/^baby of\s+/i, "").replace(/^b\/o\s+/i, "").trim()
                    || <span className="rcn-pcard-empty">if available</span>}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ══ DAY TIMELINE ══ */}
        <div className="rcn-timeline-wrap">
          <div className="rcn-timeline-header">
            <span className="rcn-timeline-label">Days</span>
            <button
              type="button"
              className="rcn-table-view-btn"
              onClick={loadTableViewData}
              title="View all filled days in a single table"
            >
              <History size={13} /> Table View
            </button>
            <div className="rcn-day1-picker rcn-day1-picker--readonly">
              <span className="rcn-day1-picker-icon" aria-hidden="true">
                <Calendar size={16} strokeWidth={1.75} />
              </span>
              <div className="rcn-day1-picker-body">
                <span className="rcn-day1-picker-label">Day 1 Date</span>
                <span className="rcn-day1-picker-value">
                  {day1Date ? formatIsoDateMedium(day1Date) : "Awaiting Form B (Date of Birth not yet recorded)"}
                </span>
              </div>
            </div>
          </div>

          <div className="rcn-timeline">
            {days.map(d => {
              const isActive    = d === activeDay;
              const isDischarge = dischargeDay && d > dischargeDay;
              const isFuture    = todayNicuDay != null && d > todayNicuDay;
              const isLocked    = isDischarge || isFuture;
              const st = helperDayDisplayStatus(
                dayStatuses[d] || STATUS.EMPTY,
                d === activeDay ? completionPct : (dayMeta[d]?.pct || 0),
              );
              const isMissed    = !isDischarge && missedDays.includes(d);
              const cfg         = DAY_STATUS_CONFIG[st] || DAY_STATUS_CONFIG[STATUS.EMPTY];
              const meta        = dayMeta[d] || {};
              // Only days that are genuinely started-but-incomplete get the
              // "what's missing" badge — a day nobody has touched yet, or one
              // that's already complete/submitted/locked, has nothing to list.
              const showMissingBadge = !isLocked && (st === STATUS.DRAFT || st === STATUS.PARTIAL || st === STATUS.LATE);
              return (
                <div key={d} className="rcn-day-wrap">
                  <button
                    type="button"
                    className={[
                      "rcn-day",
                      isActive    ? "rcn-day--active"    : "",
                      isDischarge ? "rcn-day--discharged": "",
                      isFuture    ? "rcn-day--future"    : "",
                      isMissed    ? "rcn-day--missed"    : "",
                      `rcn-day--${st}`,
                    ].filter(Boolean).join(" ")}
                    onClick={() => !isLocked && switchActiveDay(d)}
                    disabled={isFuture || isDischarge}
                    title={
                      isDischarge ? (patientInfo.status === "Died" ? `Day ${d} — Baby died` : `Day ${d} — Patient discharged`)
                      : isFuture   ? `Day ${d} — not available yet (unlocks on its calendar date)`
                      : isMissed   ? `Day ${d} — no data was ever entered (missed)`
                      : `Day ${d} · ${cfg.label}${meta.pct ? ` · ${meta.pct}%` : ""}`
                    }
                  >
                    <span className="rcn-day-label">Day {d}</span>
                    <span className="rcn-day-date">
                      {isDischarge ? "🏠" : (() => {
                        if (!day1Date) return "";
                        const base = new Date(day1Date);
                        base.setDate(base.getDate() + d - 1);
                        return base.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
                      })()}
                    </span>
                  </button>
                  {!isActive && !isFuture && !isDischarge && (isMissed || st === STATUS.LATE) && (
                    <span className="rcn-day-badge rcn-day-badge--alert" aria-hidden="true">!</span>
                  )}
                  {!isActive && !isFuture && !isDischarge && !(isMissed || st === STATUS.LATE) && showMissingBadge && (
                    <button
                      type="button"
                      className="rcn-day-badge rcn-day-badge--list"
                      onClick={(e) => handleToggleMissing(d, e)}
                      title={`Day ${d} — see what's still missing`}
                      aria-label={`See missing fields for Day ${d}`}
                    >
                      <ListChecks size={10} strokeWidth={2.5} />
                    </button>
                  )}
                  {!isActive && !isFuture && !isDischarge && !isMissed && st === STATUS.COMPLETE && (
                    <span className="rcn-day-badge rcn-day-badge--ok" title={`Day ${d} — 100% complete`} aria-hidden="true">
                      <Check size={10} strokeWidth={3} />
                    </span>
                  )}
                  {!isActive && !isFuture && !isDischarge && !isMissed && st === STATUS.SUBMITTED && (
                    <span className="rcn-day-badge rcn-day-badge--locked" title={`Day ${d} — submitted`} aria-hidden="true">
                      <Lock size={9} strokeWidth={2.5} />
                    </span>
                  )}
                </div>
              );
            })}

            {/* ── Add next day ── */}
            {!dischargeDay && (
              <button
                type="button"
                className="rcn-day-add"
                onClick={async () => {
                  const next = totalDays + 1;
                  if (isFieldEditable) {
                    try {
                      const ok = await handleSave({ silent: true });
                      if (ok === false) return;
                    } catch (err) {
                      console.error("Save before add day failed:", err);
                      return;
                    }
                  }
                  setTotalDays(next);
                  switchActiveDay(next);
                }}
                title={`Add Day ${totalDays + 1}`}
              >
                <span className="rcn-day-add-plus">+</span>
                <span className="rcn-day-add-label">Day</span>
              </button>
            )}
          </div>

          {missingPopoverDay !== null && missingPopoverPos && createPortal(
            <div
              ref={missingPopoverRef}
              className="rcn-day-missing-pop"
              style={{ top: missingPopoverPos.top, left: missingPopoverPos.left }}
            >
              <div className="rcn-day-missing-pop-head">
                <span>Day {missingPopoverDay} — missing fields</span>
                <button
                  type="button"
                  className="rcn-day-missing-pop-close"
                  onClick={() => setMissingPopoverDay(null)}
                  aria-label="Close"
                >
                  <X size={12} />
                </button>
              </div>
              {missingLoading && !missingFieldsCache[missingPopoverDay] ? (
                <p className="rcn-day-missing-pop-empty">Checking day {missingPopoverDay}&hellip;</p>
              ) : !missingFieldsCache[missingPopoverDay] || missingFieldsCache[missingPopoverDay].length === 0 ? (
                <p className="rcn-day-missing-pop-empty">Nothing missing — every field on this day is filled in.</p>
              ) : (
                <div className="rcn-day-missing-pop-body">
                  {missingFieldsCache[missingPopoverDay].map(g => (
                    <div key={g.section} className="rcn-day-missing-pop-group">
                      <p className="rcn-day-missing-pop-section">{g.section}</p>
                      <ul className="rcn-day-missing-pop-list">
                        {g.labels.map(label => <li key={label}>{label}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="rcn-day-missing-pop-goto"
                onClick={() => { switchActiveDay(missingPopoverDay); setMissingPopoverDay(null); }}
              >
                Go to Day {missingPopoverDay} <ArrowRight size={12} />
              </button>
            </div>,
            document.body
          )}

          <div className="rcn-timeline-legend">
            <div className="rcn-legend-items">
              {LEGEND_ITEMS.map(item => (
                <span
                  key={item.label}
                  className={`rcn-legend-item rcn-legend-item--${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.lock
                    ? <Lock size={11} className="rcn-legend-lock" />
                    : <span className="rcn-legend-dot" style={{ background: item.dot }} />}
                  {item.label}
                </span>
              ))}
            </div>
            <span className="rcn-legend-hint">
              <ListChecks size={12} /> Tap a partial day's badge to see what's missing
            </span>
          </div>

          {/* ── Missed-day alert ── */}
          {missedDays.length > 0 && (
            <div className="rcn-missed-banner">
              <AlertOctagon size={13} />
              <span>
                {missedDays.length} day{missedDays.length > 1 ? "s" : ""} with no data entered
                (Day {missedDays.join(", Day ")}) — still open for entry; lock them manually once reviewed.
              </span>
            </div>
          )}

          {/* ── Day 1 Date required alert — data entry is blocked below until this is set ── */}
          {!day1Date && (
            <div className="rcn-missed-banner">
              <AlertOctagon size={13} />
              <span>
                Record <strong>Date of Birth</strong> in Form B first — Day 1 Date is taken from
                that field and is required before you can enter daily helper data.
              </span>
            </div>
          )}
        </div>

        {/* ── Summary Card ── */}
        <div className="rcn-summary">
          <div className="rcn-summary-left">
            <h2 className="rcn-summary-title">
              Day {activeDay}
              {isPastActiveDay && !isSubmitted && (
                <span className="rcn-past-day-tag" title="This day's calendar date has passed, but it stays editable until you manually lock it">
                  Past day — still editable
                </span>
              )}
            </h2>
            <DayOfLifeNote dob={day1Date} tob={birthTime} day={activeDay} />
            <div className="rcn-summary-meta">
              <Clock size={13}/>
              <span>{isSaved?"Completed":"Not yet started"}</span>
            </div>
            {isSaved && (savedBy || savedAt || submittedBy || submittedAt) && (
              <p className="rcn-summary-provenance">
                {isSubmitted ? (
                  <>
                    Locked by {submittedBy || "Site User"}
                    {submittedAt ? ` · ${formatStampShort(submittedAt)}` : ""}
                    {(savedBy || savedAt) ? (
                      <>
                        {" · "}last saved by {savedBy || "Unknown"}
                        {savedAt ? ` · ${formatStampShort(savedAt)}` : ""}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    Saved by {savedBy || "Unknown"}
                    {savedAt ? ` · ${formatStampShort(savedAt)}` : ""}
                  </>
                )}
              </p>
            )}
          </div>
          <div className="rcn-summary-right">
            <div className="rcn-summary-sections">
              {[
                { emoji:"⚡", label:"Metabolic",         done:metabAnswered,  total:metabTotal  },
                { emoji:"💧", label:"Renal",             done:renalAnswered,  total:renalTotal  },
                { emoji:"🌡️", label:"Thermoregulation",  done:thermoAnswered, total:thermoTotal },
                { emoji:"🩺", label:"Vascular",          done:vascAnswered,   total:vascTotal   },
                { emoji:"👁️", label:"Eye",               done:eyeAnswered,    total:eyeTotal    },
                { emoji:"📍", label:"Location",          done:ans(tailData.location) ? 1 : 0, total: 1 },
                { emoji:"✅", label:"Survived",          done:tailData.survived_the_day === true || tailData.survived_the_day === false ? 1 : 0, total: 1 },
              ].map(s => (
                <div className="rcn-summary-section" key={s.label}>
                  <span className="rcn-summary-section-emoji">{s.emoji}</span>
                  <span className="rcn-summary-section-name">{s.label}</span>
                  <span className="rcn-summary-section-count">
                    {s.done}<span className="rcn-summary-section-total">/{s.total}</span>
                  </span>
                  <div className="rcn-summary-section-bar">
                    <div className="rcn-summary-section-bar-fill"
                      style={{ width:`${s.total>0?(s.done/s.total)*100:0}%` }}/>
                  </div>
                </div>
              ))}
            </div>
            <div className="rcn-summary-ring-wrap">
              <ProgressRing percent={completionPct}/>
              <span className="rcn-summary-ring-label">Complete</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="rcn-loading">Loading day {activeDay} data…</div>
        ) : (
          <div className="rcn-sections">

            {/* Discharge banner */}
            {dischargeDay && activeDay > dischargeDay && (
              <div className="rcn-status-banner rcn-status-banner--discharged">
                <span style={{fontSize:18}}>🏠</span>
                <div className="rcn-status-banner-text">
                  <strong>{patientInfo.status === "Died" ? "Baby died" : "Patient Discharged"}</strong>
                  <span>Day {dischargeDay} was the last NICU day. Data entry beyond this point is locked.</span>
                </div>
              </div>
            )}
            {isSubmitted && (
              <div className="rcn-status-banner rcn-status-banner--submitted">
                <Lock size={15}/>
                <div className="rcn-status-banner-text">
                  <strong>Day {activeDay} Submitted &amp; Locked</strong>
                  <span>Submitted by {submittedBy||"Site User"}{submittedAt?` · ${new Date(submittedAt).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}`:""}</span>
                </div>
              </div>
            )}
            {isSaved && !isSubmitted && completionPct < 100 && (
              <div className="rcn-status-banner rcn-status-banner--pending">
                <AlertTriangle size={15}/>
                <div className="rcn-status-banner-text">
                  <strong>{completionPct}% complete</strong>
                  <span>Fill all fields to unlock the Submit button and lock this day's data</span>
                </div>
                <span className="rcn-status-banner-badge">{totalFields-totalAnswered} remaining</span>
              </div>
            )}

            {/* ════ 4.1 METABOLIC ════ */}
            <SectionCard iconEmoji="⚡" title="4.1 Metabolic"
              answered={metabAnswered} total={metabTotal} defaultOpen={true}
              headerAction={isFieldEditable ? (
                <button
                  type="button"
                  className="rcn-refresh-helper5"
                  onClick={handleRefreshGlucoseFromHelper5}
                  disabled={!isFieldEditable || glucoseRefreshing}
                  title="Re-sync glucose #1–#4 from the Daily Monitoring Sheet for this day"
                >
                  <RefreshCw size={12} className={glucoseRefreshing ? "rcn-spin" : ""} />
                  {glucoseRefreshing ? "Refreshing…" : "Refresh from Daily Monitoring Sheet"}
                </button>
              ) : null}
            >
              <div className="rcn-yn-list">
                <GlucoseTextRow
                  label="1. Lowest glucose reading (if <45 mg/dL)"
                  value={metabData.lowest_glucose}
                  onChange={v => setGlucoseField("lowest_glucose", v)}
                  disabled={!isFieldEditable}
                  unit="mg/dL"
                  autofilled={!!glucoseAutofilled.lowest_glucose}
                  placeholder="e.g. 40"
                  allowNotTested={true}
                />
                <NumRow
                  label="2. No of episodes of hypoglycemia"
                  value={metabData.hypoglycemia_episodes}
                  onChange={v => setGlucoseField("hypoglycemia_episodes", v)}
                  disabled={!isFieldEditable}
                  placeholder="0"
                  autofilled={!!glucoseAutofilled.hypoglycemia_episodes}
                />
                {hypoRxRequired && (
                  <div className="rcn-conditional-block">
                    <YNRow label="3. Hypoglycemia Rx (required — low reading present)" value={metabData.hypoglycemia_rx}
                      onChange={v=>setMetab("hypoglycemia_rx",v)} disabled={!isFieldEditable}/>
                  </div>
                )}
                <GlucoseTextRow
                  label="4. Highest glucose reading (if >125 mg/dL)"
                  value={metabData.highest_glucose}
                  onChange={v => setGlucoseField("highest_glucose", v)}
                  disabled={!isFieldEditable}
                  unit="mg/dL"
                  autofilled={!!glucoseAutofilled.highest_glucose}
                  placeholder="e.g. 200"
                  allowNotTested={true}
                />
                {hyperRxRequired && (
                  <div className="rcn-conditional-block">
                    <YNRow label="5. Hyperglycemia Rx (Insulin)" value={metabData.insulin}
                      onChange={v=>setMetab("insulin",v)} disabled={!isFieldEditable}/>
                  </div>
                )}

                <div className="rcn-readings-field">
                  <div className="rcn-field-label-row">
                    <span className="rcn-yn-label">6. Metabolic acidosis (pH&lt;7.2) — enter readings</span>
                    <StatusToggleGroup status={metabData.metabolic_acidosis_status}
                      onChange={s => setAcidosisStatus(s)}
                      disabled={!isFieldEditable} />
                  </div>
                  {metabData.metabolic_acidosis_status === STATUS_NOT_DONE ? (
                    <div className="rcn-field-sub">{STATUS_NOT_DONE}</div>
                  ) : (
                  <ReadingsBlock
                    code="pH"
                    fixedDate={activeDayDate || ""}
                    entries={metabData.ph_readings}
                    disabled={!isFieldEditable}
                    blankFactory={() => blankReading({ ph: "" })}
                    onChangeEntry={(i, k, v) => setReadingField("ph_readings", i, k, v, "ph")}
                    onAdd={blank => addReading("ph_readings", blank, "ph")}
                    onRemove={i => removeReading("ph_readings", i, "ph")}
                  >
                    {(e, i) => (
                      <label className="mml-meta-field">
                        <span>pH</span>
                        <div className="rcn-num-input">
                          <input type="number" step="0.01" value={e.ph ?? ""}
                            disabled={!isFieldEditable}
                            onChange={ev => setReadingField("ph_readings", i, "ph",
                              ev.target.value === "" ? "" : Number(ev.target.value), "ph")} />
                        </div>
                      </label>
                    )}
                  </ReadingsBlock>
                  )}
                  <div className="rcn-yn-list" style={{ marginTop: 8 }}>
                    <ReadonlyAutoField
                      label="Metabolic acidosis (auto)"
                      value={
                        metabData.metabolic_acidosis_status === STATUS_NOT_DONE ? "Not Done"
                          : metabData.metabolic_acidosis === true ? "Yes"
                            : metabData.metabolic_acidosis === false ? "No"
                              : "—"
                      }
                    />
                  </div>
                </div>

                <div className="rcn-readings-field">
                  <div className="rcn-field-label-row">
                    <span className="rcn-yn-label">7. Sodium value (&lt;135 or &gt;142)</span>
                    <StatusToggleGroup status={labStatus("sodium_value")}
                      onChange={s => setLabStatus("sodium_value", s)}
                      disabled={!isFieldEditable} allowAwaited={true} />
                  </div>
                  {labStatus("sodium_value") ? (
                    <div className="rcn-field-sub">{labStatus("sodium_value")}</div>
                  ) : (
                  <ReadingsBlock
                    code="Na"
                    fixedDate={activeDayDate || ""}
                    entries={metabData.sodium_readings}
                    disabled={!isFieldEditable}
                    blankFactory={() => blankReading({ value: "" })}
                    onChangeEntry={(i, k, v) => setReadingField("sodium_readings", i, k, v)}
                    onAdd={blank => addReading("sodium_readings", blank)}
                    onRemove={i => removeReading("sodium_readings", i)}
                  >
                    {(e, i) => (
                      <label className="mml-meta-field">
                        <span>Value</span>
                        <div className="rcn-num-input">
                          <input type="number" step="0.01" value={e.value ?? ""}
                            disabled={!isFieldEditable}
                            onChange={ev => setReadingField("sodium_readings", i, "value",
                              ev.target.value === "" ? "" : Number(ev.target.value))} />
                          <span className="rcn-num-unit">mmol/L</span>
                        </div>
                      </label>
                    )}
                  </ReadingsBlock>
                  )}
                </div>

                <div className="rcn-readings-field">
                  <div className="rcn-field-label-row">
                    <span className="rcn-yn-label">8. Potassium value (&lt;3.5 or &gt;6)</span>
                    <StatusToggleGroup status={labStatus("potassium_value")}
                      onChange={s => setLabStatus("potassium_value", s)}
                      disabled={!isFieldEditable} allowAwaited={true} />
                  </div>
                  {labStatus("potassium_value") ? (
                    <div className="rcn-field-sub">{labStatus("potassium_value")}</div>
                  ) : (
                  <ReadingsBlock
                    code="K"
                    fixedDate={activeDayDate || ""}
                    entries={metabData.potassium_readings}
                    disabled={!isFieldEditable}
                    blankFactory={() => blankReading({ value: "" })}
                    onChangeEntry={(i, k, v) => setReadingField("potassium_readings", i, k, v)}
                    onAdd={blank => addReading("potassium_readings", blank)}
                    onRemove={i => removeReading("potassium_readings", i)}
                  >
                    {(e, i) => (
                      <label className="mml-meta-field">
                        <span>Value</span>
                        <div className="rcn-num-input">
                          <input type="number" step="0.01" value={e.value ?? ""}
                            disabled={!isFieldEditable}
                            onChange={ev => setReadingField("potassium_readings", i, "value",
                              ev.target.value === "" ? "" : Number(ev.target.value))} />
                          <span className="rcn-num-unit">mmol/L</span>
                        </div>
                      </label>
                    )}
                  </ReadingsBlock>
                  )}
                </div>

                <div className="rcn-readings-field">
                  <div className="rcn-field-label-row">
                    <span className="rcn-yn-label">9. Ionized Calcium value (&lt;0.9 or &gt;1.2)</span>
                    <StatusToggleGroup status={labStatus("ionized_calcium_value")}
                      onChange={s => setLabStatus("ionized_calcium_value", s)}
                      disabled={!isFieldEditable} allowAwaited={true} />
                  </div>
                  {labStatus("ionized_calcium_value") ? (
                    <div className="rcn-field-sub">{labStatus("ionized_calcium_value")}</div>
                  ) : (
                  <ReadingsBlock
                    code="iCa"
                    fixedDate={activeDayDate || ""}
                    entries={metabData.calcium_readings}
                    disabled={!isFieldEditable}
                    blankFactory={() => blankReading({ value: "" })}
                    onChangeEntry={(i, k, v) => setReadingField("calcium_readings", i, k, v)}
                    onAdd={blank => addReading("calcium_readings", blank)}
                    onRemove={i => removeReading("calcium_readings", i)}
                  >
                    {(e, i) => (
                      <label className="mml-meta-field">
                        <span>Value</span>
                        <div className="rcn-num-input">
                          <input type="number" step="0.01" value={e.value ?? ""}
                            disabled={!isFieldEditable}
                            onChange={ev => setReadingField("calcium_readings", i, "value",
                              ev.target.value === "" ? "" : Number(ev.target.value))} />
                          <span className="rcn-num-unit">mmol/L</span>
                        </div>
                      </label>
                    )}
                  </ReadingsBlock>
                  )}
                </div>

                <YNRow label="10. Osteopenia suspected" value={metabData.osteopenia_suspected}
                  onChange={v=>setMetab("osteopenia_suspected",v)} disabled={!isFieldEditable}/>
              </div>
            </SectionCard>

            {/* ════ 4.2 RENAL ════ */}
            <SectionCard iconEmoji="💧" title="4.2 Renal"
              answered={renalAnswered} total={renalTotal} defaultOpen={true}>
              <div className="rcn-yn-list">
                <YNRow label="11. AKI suspected" value={renalData.aki_suspected}
                  onChange={v=>setRenal("aki_suspected",v)} disabled={!isFieldEditable}/>
              </div>

              <div className="rcn-yn-list" style={{ marginTop: 12 }}>
                <GlucoseTextRow
                  label="12. Serum Creatinine (mg/dL)"
                  value={renalData.creatinine_value}
                  onChange={v => setRenal("creatinine_value", v)}
                  disabled={!isFieldEditable}
                  unit="mg/dL"
                  placeholder="value"
                  allowAwaited={true}
                  allowNotDone={true}
                />
                <NumRow label="13a. Urine output 8am → 2pm" value={renalData.urine_output_8am_2pm}
                  onChange={v=>setRenal("urine_output_8am_2pm",v)} disabled={!isFieldEditable}
                  unit="ml/kg/hr"
                  status={renalData.urine_output_8am_2pm_status}
                  onStatusChange={v=>setRenal("urine_output_8am_2pm_status",v)}/>
                <NumRow label="13b. Urine output 2pm → 8pm" value={renalData.urine_output_2pm_8pm}
                  onChange={v=>setRenal("urine_output_2pm_8pm",v)} disabled={!isFieldEditable}
                  unit="ml/kg/hr"
                  status={renalData.urine_output_2pm_8pm_status}
                  onStatusChange={v=>setRenal("urine_output_2pm_8pm_status",v)}/>
                <NumRow label="13c. Urine output 8pm → 8am" value={renalData.urine_output_8pm_8am}
                  onChange={v=>setRenal("urine_output_8pm_8am",v)} disabled={!isFieldEditable}
                  unit="ml/kg/hr"
                  status={renalData.urine_output_8pm_8am_status}
                  onStatusChange={v=>setRenal("urine_output_8pm_8am_status",v)}/>
                <ReadonlyAutoField
                  label="13. Urine output total (sum)"
                  value={renalData.urine_output_total}
                  unit="ml/kg/hr"
                />
                <YNRow label="14. Dialysis/CRRT" value={renalData.dialysis_crrt}
                  onChange={v=>setRenal("dialysis_crrt",v)} disabled={!isFieldEditable}/>
              </div>
            </SectionCard>

            {/* ════ 4.3 THERMOREGULATION ════ */}
            <SectionCard iconEmoji="🌡️" title="4.3 Thermoregulation"
              answered={thermoAnswered} total={thermoTotal} defaultOpen={true}>
              <div className="rcn-yn-list">
                <NumRow label="15. Axillary Temperature (<36.5 or >37.5)" value={thermoData.axillary_temperature}
                  onChange={v=>setThermo("axillary_temperature",v)} disabled={!isFieldEditable} unit="°C"/>
              </div>
            </SectionCard>

            {/* ════ 4.4 VASCULAR ACCESS ════ */}
            <SectionCard iconEmoji="🩺" title="4.4 Vascular Access"
              answered={vascAnswered} total={vascTotal} defaultOpen={true}>
              <div className="rcn-yn-list">
                <YNRow label="16. PICC in situ"           value={vascData.picc_in_situ}         onChange={v=>setVasc("picc_in_situ",v)}         disabled={!isFieldEditable}/>
                <YNRow label="17. UVC in situ"            value={vascData.uvc_in_situ}          onChange={v=>setVasc("uvc_in_situ",v)}          disabled={!isFieldEditable}/>
                <YNRow label="18. UAC in situ"            value={vascData.uac_in_situ}          onChange={v=>setVasc("uac_in_situ",v)}          disabled={!isFieldEditable}/>
                <YNRow label="19. Peripheral IV"          value={vascData.peripheral_iv}        onChange={v=>setVasc("peripheral_iv",v)}        disabled={!isFieldEditable}/>
                <YNRow label="20. Peripheral arterial"    value={vascData.peripheral_arterial}  onChange={v=>setVasc("peripheral_arterial",v)}  disabled={!isFieldEditable}/>
                {extravasationRequired && (
                  <div className="rcn-conditional-block">
                    <YNRow label="21. Extravasation injury" value={vascData.extravasation_injury}
                      onChange={v=>setVasc("extravasation_injury",v)} disabled={!isFieldEditable}/>
                  </div>
                )}
                <YNRow label="22. Line complication"      value={vascData.line_complication}    onChange={v=>setVasc("line_complication",v)}    disabled={!isFieldEditable}/>
              </div>
            </SectionCard>

            {/* ════ 4.5 OPHTHALMOLOGY ════ */}
            <SectionCard iconEmoji="👁️" title="4.5 Ophthalmology"
              answered={eyeAnswered} total={eyeTotal} defaultOpen={true}>
              <div className="rcn-yn-list">
                <YNRow label="23. ROP screening due" value={eyeData.rop_screening_due}
                  onChange={v=>setEye("rop_screening_due",v)} disabled={!isFieldEditable}/>
                <YNRow label="24. ROP screened" value={eyeData.rop_screened}
                  onChange={v=>setEye("rop_screened",v)} disabled={!isFieldEditable}/>
                {ropScreenedYes && (
                  <div className="rcn-conditional-block">
                    <YNRow label="25. ROP detected" value={eyeData.rop_detected}
                      onChange={v=>setEye("rop_detected",v)} disabled={!isFieldEditable}/>
                  </div>
                )}
              </div>

              {ropYes && (
                <div className="rcn-subsection">
                  <div className="rcn-subsection-title">Optional detail (not on paper CRF)</div>
                  <div className="rcn-yn-list" style={{marginTop:16}}>
                    <YNRow label="Plus Disease"   value={eyeData.plus_disease}   onChange={v=>setEye("plus_disease",v)}   disabled={!isFieldEditable}/>
                    <YNRow label="ROP Treatment"  value={eyeData.rop_treatment}  onChange={v=>setEye("rop_treatment",v)}  disabled={!isFieldEditable}/>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* ════ 4.6 LOCATION ════ */}
            <SectionCard iconEmoji="📍" title="4.6 Location"
              answered={ans(tailData.location) ? 1 : 0} total={1} defaultOpen={true}>
              <PillMulti
                options={helperLocationOptions({ selected: tailData.location })}
                labelOf={opt => locationLabel(opt, { enrollmentId, site: patientData?.site_name || patientData?.site })}
                value={tailData.location || []}
                onChange={v => setTail("location", v)}
                disabled={!isFieldEditable}
              />
            </SectionCard>

            {/* ════ 4.7 SURVIVED THE DAY ════ */}
            <SectionCard iconEmoji="✅" title="4.7 Survived the Day"
              answered={tailData.survived_the_day === true || tailData.survived_the_day === false ? 1 : 0}
              total={1} defaultOpen={true}>
              <div className="rcn-yn-list">
                <YNRow label="Survived the day" value={tailData.survived_the_day}
                  onChange={v=>setTail("survived_the_day",v)} disabled={!isFieldEditable}/>
              </div>
              {tailData.survived_the_day === false && laterDaysSaved.length > 0 && (
                <div className="field-hint field-hint-warning">
                  Day {laterDaysSaved.join(", ")} already {laterDaysSaved.length > 1 ? "have" : "has"} entries after this
                  day. They will be raised as a data query.
                </div>
              )}
            </SectionCard>

          </div>
        )}

        {message && (
          <div className={`form-message${message.startsWith("✅")||message.startsWith("🔒")?" form-message--success":" form-message--error"}`}>
            {message}
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <SubmitModal day={activeDay} completionPct={completionPct}
          onConfirm={handleSubmit} onCancel={() => setShowModal(false)} submitting={submitting}/>
      )}

      {/* ══ TABLE VIEW MODAL ══ */}
      {showTableView && (
        <div className="rcn-modal-overlay" onClick={() => setShowTableView(false)}>
          <div className="rcn-modal rcn-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="rcn-modal-header">
              <div className="rcn-modal-icon"><History size={18} /></div>
              <div>
                <h3 className="rcn-modal-title">All Days — Table View</h3>
                <p className="rcn-modal-subtitle">Every day filled in so far for this baby, side by side</p>
              </div>
              <button className="rcn-modal-close" type="button" onClick={() => setShowTableView(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="rcn-modal-body rcn-table-view-body">
              {tableViewLoading ? (
                <p className="rcn-table-view-empty">Loading all days&hellip;</p>
              ) : tableViewRows.length === 0 ? (
                <p className="rcn-table-view-empty">No days have been filled in yet.</p>
              ) : (
                <div className="rcn-table-view-scroll">
                  <table className="rcn-table-view rcn-table-view--vertical">
                    <thead>
                      <tr>
                        <th className="rcn-table-view-field-header">Field</th>
                        {tableViewRows.map(({ day }) => {
                          const st  = helperDayDisplayStatus(
                            dayStatuses[day] || STATUS.EMPTY,
                            dayMeta[day]?.pct || 0,
                          );
                          const cfg = DAY_STATUS_CONFIG[st] || DAY_STATUS_CONFIG[STATUS.EMPTY];
                          return (
                            <th key={day} className="rcn-table-view-day-header">
                              <button
                                type="button"
                                className="rcn-table-view-goto-btn"
                                onClick={() => { switchActiveDay(day); setShowTableView(false); }}
                                title="Go to this day"
                              >
                                Day {day}
                              </button>
                              <span className="rcn-table-view-day-status">
                                <span className="rcn-table-view-status-dot" style={{ background: cfg.dot }} />
                                {cfg.label}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {TABLE_VIEW_FIELD_GROUPS.map(group => (
                        <React.Fragment key={group.section}>
                          <tr className="rcn-table-view-section-row">
                            <td colSpan={tableViewRows.length + 1}>{group.section}</td>
                          </tr>
                          {group.rows.map(row => (
                            <tr key={row.key}>
                              <td className="rcn-table-view-field-cell">{row.label}</td>
                              {tableViewRows.map(({ day, data: d }) => (
                                <td key={day}>{formatTableViewValue(d, row)}</td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ AUDIT TRAIL MODAL ══ */}
      {showAuditModal && (
        <div className="rcn-modal-overlay" onClick={() => setShowAuditModal(false)}>
          <div className="rcn-modal" onClick={e => e.stopPropagation()}>
            <div className="rcn-modal-header">
              <div className="rcn-modal-icon"><History size={18} /></div>
              <div>
                <h3 className="rcn-modal-title">Day {activeDay} History</h3>
                <p className="rcn-modal-subtitle">Every save, submit, and override for this day</p>
              </div>
              <button className="rcn-modal-close" type="button" onClick={() => setShowAuditModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="rcn-modal-body">
              {auditLoading ? (
                <div className="rcn-audit-empty">Loading…</div>
              ) : auditEntries.length === 0 ? (
                <div className="rcn-audit-empty">No history recorded for this day yet.</div>
              ) : (
                <div className="rcn-audit-list">
                  {auditEntries.map(e => (
                    <div key={e.id} className="rcn-audit-entry">
                      <div className="rcn-audit-entry-top">
                        <span className="rcn-audit-action">{auditActionLabel(e.action)}</span>
                        <span className="rcn-audit-time">
                          {e.created_at ? new Date(e.created_at).toLocaleString("en-GB") : ""}
                        </span>
                      </div>
                      <span className="rcn-audit-user">by {e.username || "unknown"}</span>
                      {e.new_values?.reason && (
                        <p className="rcn-audit-reason">"{e.new_values.reason}"</p>
                      )}
                      <AuditChangeList
                        oldValues={e.old_values}
                        newValues={e.new_values}
                        compact
                        hideKeys={["reason", "nicu_day"]}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ SITE-MONITOR OVERRIDE MODAL ══ */}
      {showOverrideModal && (
        <div className="rcn-modal-overlay" onClick={() => !overrideSubmitting && setShowOverrideModal(false)}>
          <div className="rcn-modal" onClick={e => e.stopPropagation()}>
            <div className="rcn-modal-header">
              <div className="rcn-modal-icon"><Unlock size={18} /></div>
              <div>
                <h3 className="rcn-modal-title">Override &amp; Unlock Day {activeDay}</h3>
                <p className="rcn-modal-subtitle">Temporarily reopens this locked day for a correction</p>
              </div>
              <button className="rcn-modal-close" type="button" onClick={() => setShowOverrideModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="rcn-modal-body">
              <p style={{ fontSize: 12.5, color: "#475569", marginTop: 0 }}>
                This reopens Day {activeDay} for 2 hours so it can be corrected. The reason below
                is saved to the audit trail.
              </p>
              <textarea
                className="rcn-override-textarea"
                placeholder="Reason for correction (required)…"
                value={overrideReason}
                onChange={e => setOverrideReason(e.target.value)}
              />
            </div>
            <div className="rcn-modal-footer">
              <button className="rcn-modal-btn rcn-modal-btn--cancel" type="button"
                onClick={() => setShowOverrideModal(false)} disabled={overrideSubmitting}>
                Cancel
              </button>
              <button
                className="rcn-modal-btn rcn-modal-btn--confirm"
                type="button"
                disabled={!overrideReason.trim() || overrideSubmitting}
                onClick={async () => {
                  setOverrideSubmitting(true);
                  try {
                    const res = await api.patch(
                      `/metab-renal-vasc-eye/${enrollmentId}/${activeDay}/override-unlock`,
                      { reason: overrideReason.trim(), hours: 2 }
                    );
                    setOverrideUntil(res?.data?.override_unlocked_until || null);
                    // isFieldEditable also requires isEditing when isSaved is
                    // true (always true for a submitted day) — without this,
                    // the override succeeds server-side but fields still
                    // render read-only and every setter silently no-ops.
                    setIsEditing(true);
                    setOverrideReason("");
                    setShowOverrideModal(false);
                    setMessage(`🔓 Day ${activeDay} reopened for 2 hours`);
                  } catch (err) {
                    setMessage("⚠️ Could not unlock — " + (err?.response?.data?.detail || "try again"));
                  } finally {
                    setOverrideSubmitting(false);
                  }
                }}
              >
                {overrideSubmitting ? "Unlocking…" : "Unlock Day"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sticky Footer ── */}
      <div className="form-navigation">
        <button type="button" className="btn btn-secondary btn-outline"
          onClick={handlePrevious}>
          <ArrowLeft size={15}/> Infect-GI-Hema
        </button>

        {/* History — superadmin + site PI only, any day/lock state */}
        {canViewAudit && (
          <button
            type="button"
            className="btn rcn-history-btn"
            onClick={fetchAuditHistory}
            title={`View correction history for Day ${activeDay}`}
          >
            <History size={15}/> History
          </button>
        )}

        {/* Save — always visible when editing */}
        {isFieldEditable && (
          <button type="button" className="btn btn-save btn-outline-blue" onClick={handleSave}>
            <Save size={15}/> Save
          </button>
        )}

        {/* Edit button — enable editing of saved draft before Lock */}
        {isSaved && !isEditing && !isSubmitted && !isAfterStay && !isFutureActiveDay && (
          <button
            type="button"
            className="btn btn-edit btn-outline-blue"
            onClick={() => setIsEditing(true)}
            title="Enable editing of saved data"
          >
            <Edit size={13} /> Edit Day {activeDay}
          </button>
        )}

        {/* Save / manual Lock button / Locked badge — locking is explicit
            and manual only: nothing here auto-locks a day just because its
            calendar date has passed. A day stays editable until a user
            clicks "Lock" and confirms in the modal. */}
        {isOverrideActiveDay ? (
          <>
            <div className="rcn-locked-badge rcn-locked-badge--override" title="Temporarily reopened by a site monitor">
              <Unlock size={13} /> Day {activeDay} Reopened (Override)
            </div>
            {canSubmit ? (
              <button type="button" className="btn btn-lock-day" onClick={() => setShowModal(true)}
                title="Lock this day — you won't be able to edit it after confirming">
                <Lock size={15} /> Lock Day {activeDay}
              </button>
            ) : (
              <button type="button" className="btn btn-draft" onClick={handleSave}>
                <Save size={15} /> Save Correction
              </button>
            )}
          </>
        ) : isSubmitted ? (
          <>
            <div className="rcn-locked-badge"><Lock size={13}/> Day {activeDay} Locked</div>
            {isSuperadmin && (
              <button
                type="button"
                className="rcn-override-btn"
                onClick={() => setShowOverrideModal(true)}
                title="Reopen this locked day temporarily for a correction"
              >
                <Unlock size={13}/> Override &amp; Unlock
              </button>
            )}
          </>
        ) : isFutureActiveDay ? (
          <div className="rcn-locked-badge" title="Data can only be entered on the day's own calendar date">
            <Lock size={13}/> Day {activeDay} Not Available Yet
          </div>
        ) : (
          <>
            {!isAfterStay && (
              <button type="button" className="btn btn-draft" onClick={handleSave}>
                <Save size={15}/> Save for Later
              </button>
            )}
            {canSubmit && (
              <button
                type="button"
                className="btn btn-lock-day"
                onClick={() => setShowModal(true)}
                title="Lock this day — you won't be able to edit it after confirming"
              >
                <Lock size={15}/> Lock Day {activeDay}
              </button>
            )}
          </>
        )}

        <div className="footer-step-indicator">
          <span className="step-text">HELPER 4 OF 4</span>
          <div className="step-progress-line">
            <div className="progress-segment active"/>
            <div className="progress-segment active"/>
            <div className="progress-segment active"/>
            <div className="progress-segment active"/>
          </div>
        </div>
        <button type="button" className="btn btn-primary"
          onClick={handleNext} disabled={!isSaved}>
          Form F <ArrowRight size={15}/>
        </button>
      </div>
      <SaveSuccessModal
        open={showSaveSuccess}
        onClose={() => setShowSaveSuccess(false)}
        message={`Day ${activeDay} has been saved successfully.`}
      />
    </>
  );
}
